import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Keyboard,
  KeyboardAvoidingView,
  Modal,
  Platform,
  RefreshControl,
  ScrollView,
  TextInput as NativeTextInput,
  useWindowDimensions,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useFocusEffect } from 'expo-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@/lib/styling';
import { Theme } from '@/theme';

import { trpc, useTRPCClient } from '@/lib/trpc';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Switch } from '@/components/ui/Switch';
import { Textarea } from '@/components/ui/Textarea';
import { Box, Text, PressableBox } from '@/components/ui/primitives';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { useLeagues } from '@/hooks/useLeagues';
import { isUnauthorizedError } from '@/lib/authUtils';
import AppHeader from '@/components/AppHeader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { AppSelect } from '@/components/ui/AppSelect';
import { isIOS } from '@/lib/platform';
import { clientLogger } from '@/lib/datadog';
import { getCurrentTime } from '@/utils/timeUtils';
import { PortalHost } from '@rn-primitives/portal';
import { Plus, Trash2 } from 'lucide-react-native';

const minWidth90Style = { minWidth: 90 };
const flexStyle = { flex: 1 };
const betEditorScrollContentStyle = { paddingHorizontal: 24, paddingTop: 20, paddingBottom: 40, flexGrow: 0 };
const minWidth120Style = { minWidth: 120 };

export function BetsCreatorRestrictedState() {
  return (
    <Box flex={1} backgroundColor="background">
      <AppHeader title="Bets Creator" />
      <Box gap="m" paddingHorizontal="l" paddingBottom="xl" paddingTop="m">
        <Card>
          <CardHeader>
            <CardTitle>
              <Text variant="title" fontWeight="600" color="text">Bets Creator</Text>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Text color="mutedForeground">
              Only Bets Admins can access the bets creator tools.
            </Text>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

interface NflTeam {
  id: string;
  name: string;
  city: string;
  abbreviation: string;
}

interface NflGame {
  id: string;
  weekId: string;
  homeTeamId: string;
  awayTeamId: string;
  gameTime: string;
  isCompleted?: boolean | null;
  homeTeam?: NflTeam | null;
  awayTeam?: NflTeam | null;
}

interface CustomPropOption {
  label: string;
  odds: number;
  side: string;
  playerId?: string;
  outcome?: 'none' | 'player';
}

interface CustomPropDisplayInfo {
  label: string;
  description?: string;
  sideLabelOne: string;
  sideLabelTwo: string;
  winningSide?: 'one' | 'two' | 'push' | string;
  outcomeLabel?: string;
  options?: CustomPropOption[];
  sport?: string;
  competition?: string;
  propSubtype?: string;
  teamSide?: 'home' | 'away';
  statsProvider?: string;
  oddsProvider?: string;
  sourceMatchId?: string | number;
  sourceMarketKey?: string;
  manualResolutionAllowed?: boolean;
  requiresManualResolution?: boolean;
  manualResolutionReason?: string;
  roundProp?: boolean;
  roundPropMetric?: string;
  roundGameIds?: string[];
  threshold?: number;
}

interface CustomPropBet {
  id: string;
  nflGameId: string;
  nflBetType: 'custom_prop';
  oddsOne: number | null;
  oddsTwo: number | null;
  propDefinitionId?: string | null;
  displayInfo: CustomPropDisplayInfo;
  createdAt: string;
  updatedAt: string;
  game: NflGame & { homeTeam: NflTeam | null; awayTeam: NflTeam | null };
}

interface CustomPropBetRecord {
  id: string;
  nflGameId: string;
  propDefinitionId?: string | null;
  oddsOne?: number | null;
  oddsTwo?: number | null;
  displayInfo: CustomPropDisplayInfo;
  createdAt?: string;
  updatedAt?: string;
}

interface FormState {
  gameId: string;
  label: string;
  description: string;
  options: { side: string; label: string; odds: string; playerId?: string; outcome?: 'none' | 'player' }[];
  winningSide: string;
  outcomeLabel: string;
  propType: string;
  statType: string;
  threshold: string;
  teamSide: string;
  playerIds: string;
}

const SCORING_OVERRIDE_SIDES = [
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
] as const;

const SCORING_OVERRIDE_SIDE_SET = new Set<string>(SCORING_OVERRIDE_SIDES);
const PROP_SIDE_KEYS = SCORING_OVERRIDE_SIDES;

const getOverrideSides = (displayInfo?: CustomPropDisplayInfo | null) => {
  if (displayInfo?.options && Array.isArray(displayInfo.options) && displayInfo.options.length > 0) {
    const sides = displayInfo.options
      .map((option, index) => {
        const side = typeof option.side === 'string' ? option.side.trim() : '';
        const label = typeof option.label === 'string' ? option.label.trim() : '';
        return {
          side,
          label: label || `Side ${index + 1}`,
        };
      })
      .filter((entry) => SCORING_OVERRIDE_SIDE_SET.has(entry.side));
    if (sides.length > 0) {
      return sides;
    }
  }

  return [
    {
      side: 'one',
      label:
        typeof displayInfo?.sideLabelOne === 'string' && displayInfo.sideLabelOne.trim()
          ? displayInfo.sideLabelOne.trim()
          : 'Side 1',
    },
    {
      side: 'two',
      label:
        typeof displayInfo?.sideLabelTwo === 'string' && displayInfo.sideLabelTwo.trim()
          ? displayInfo.sideLabelTwo.trim()
          : 'Side 2',
    },
  ];
};

interface PlayerSearchResult {
  id: string;
  fullName: string;
  teamAbbr: string;
  position: string;
  jerseyNumber: number | null;
}

const formatGameLabel = (game: NflGame) => {
  const away = game.awayTeam?.abbreviation ?? game.awayTeam?.name ?? 'Away';
  const home = game.homeTeam?.abbreviation ?? game.homeTeam?.name ?? 'Home';
  return `${away} @ ${home}`;
};

const formatGameTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'TBD';
  }
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const PROP_TYPES_REQUIRING_STAT_TYPE = [
  'PLAYER_VS_PLAYER_YARDS',
  'PLAYER_OVER_UNDER_YARDS',
  'PLAYER_EITHER_REACHES_THRESHOLD',
  'LONGEST_STAT',
  'FIRST_PLAYER_TO_STAT',
  'SOCCER_PLAYER_OVER_UNDER',
  'SOCCER_PLAYER_EACH_HALF_OVER_UNDER',
  'SOCCER_PLAYERS_COMBINED_OVER_UNDER',
  'SOCCER_ALL_PLAYERS_OVER_UNDER',
  'SOCCER_PLAYER_RACE_TO_STAT',
  'SOCCER_TEAM_RACE_TO_STAT',
  'SOCCER_BOTH_TEAMS_OVER_STAT',
  'SOCCER_PLAYER_STAT_COMPARISON',
  'SOCCER_TEAM_STAT_COMPARISON',
];

const PROP_TYPES_REQUIRING_THRESHOLD = [
  'PLAYER_OVER_UNDER_YARDS',
  'PLAYER_EITHER_REACHES_THRESHOLD',
  'PLAYER_OVER_UNDER_RECEPTIONS',
  'PLAYER_OVER_UNDER_CARRIES',
  'PLAYER_OVER_UNDER_INTERCEPTIONS',
  'PLAYER_OVER_UNDER_SACKS',
  'LONGEST_STAT',
  'SOCCER_PLAYER_OVER_UNDER',
  'SOCCER_PLAYER_EACH_HALF_OVER_UNDER',
  'SOCCER_PLAYERS_COMBINED_OVER_UNDER',
  'SOCCER_ALL_PLAYERS_OVER_UNDER',
  'SOCCER_PLAYER_RACE_TO_STAT',
  'SOCCER_TEAM_RACE_TO_STAT',
  'SOCCER_BOTH_TEAMS_OVER_STAT',
  'SOCCER_CORNERS_OVER_UNDER',
  'SOCCER_FIRST_TEAM_TO_CORNERS',
  'SOCCER_FOULS_OVER_UNDER',
  'SOCCER_TEAM_FOULS_OVER_UNDER',
  'SOCCER_OFFSIDES_OVER_UNDER',
  'SOCCER_TEAM_OFFSIDES_OVER_UNDER',
  'SOCCER_TEAM_GOALS_ALLOWED_OVER_UNDER',
  'SOCCER_TEAM_SHOTS_ON_TARGET_PCT_OVER_UNDER',
  'SOCCER_ROUND_EXTRA_TIME_OVER_UNDER',
  'SOCCER_ROUND_PENALTY_SHOOTOUT_OVER_UNDER',
  'SOCCER_ROUND_TOTAL_GOALS_OVER_UNDER',
  'SOCCER_ROUND_SCORELESS_REGULATION_TEAMS_OVER_UNDER',
  'SOCCER_ROUND_PENALTY_CARDS_OVER_UNDER',
  'SOCCER_ROUND_RED_CARDS_OVER_UNDER',
];

const GAME_LEVEL_SOCCER_PROP_TYPES = [
  'SOCCER_BOTH_TEAMS_TO_SCORE',
  'SOCCER_TEAM_RACE_TO_STAT',
  'SOCCER_BOTH_TEAMS_OVER_STAT',
  'SOCCER_CORNERS_OVER_UNDER',
  'SOCCER_FIRST_TEAM_TO_CORNERS',
  'SOCCER_FOULS_OVER_UNDER',
  'SOCCER_TEAM_FOULS_OVER_UNDER',
  'SOCCER_OFFSIDES_OVER_UNDER',
  'SOCCER_TEAM_OFFSIDES_OVER_UNDER',
  'SOCCER_TEAM_GOALS_ALLOWED_OVER_UNDER',
  'SOCCER_GOALS_ODD_EVEN',
  'SOCCER_METHOD_OF_VICTORY',
  'SOCCER_FIRST_GOAL_INTERVAL',
  'SOCCER_FIRST_GOAL_METHOD',
  'SOCCER_TEAM_STAT_COMPARISON',
];

const SOCCER_PROP_TYPES_REQUIRING_TEAM_SIDE = [
  'SOCCER_TEAM_FOULS_OVER_UNDER',
  'SOCCER_TEAM_OFFSIDES_OVER_UNDER',
  'SOCCER_TEAM_GOALS_ALLOWED_OVER_UNDER',
  'SOCCER_TEAM_SHOTS_ON_TARGET_PCT_OVER_UNDER',
];

const ROUND_PROP_TYPES: RoundPropMetric[] = [
  'SOCCER_ROUND_EXTRA_TIME_OVER_UNDER',
  'SOCCER_ROUND_PENALTY_SHOOTOUT_OVER_UNDER',
  'SOCCER_ROUND_TOTAL_GOALS_OVER_UNDER',
  'SOCCER_ROUND_SCORELESS_REGULATION_TEAMS_OVER_UNDER',
  'SOCCER_ROUND_PENALTY_CARDS_OVER_UNDER',
];

const ROUND_PROP_LABELS: Record<RoundPropMetric, { label: string; description: string; metric: string }> = {
  SOCCER_ROUND_EXTRA_TIME_OVER_UNDER: {
    label: 'Games To Extra Time',
    description: 'How many games in this round will go to extra time?',
    metric: 'extra_time_games',
  },
  SOCCER_ROUND_PENALTY_SHOOTOUT_OVER_UNDER: {
    label: 'Games To Penalty Kicks',
    description: 'How many games in this round will go to a penalty shootout?',
    metric: 'penalty_shootout_games',
  },
  SOCCER_ROUND_TOTAL_GOALS_OVER_UNDER: {
    label: 'Total Round Goals',
    description: 'Total goals scored across every game in this round.',
    metric: 'total_goals',
  },
  SOCCER_ROUND_SCORELESS_REGULATION_TEAMS_OVER_UNDER: {
    label: 'Scoreless Regulation Teams',
    description: 'Number of teams that finish regulation without scoring in this round.',
    metric: 'scoreless_regulation_teams',
  },
  SOCCER_ROUND_PENALTY_CARDS_OVER_UNDER: {
    label: 'Total Round Penalty Cards',
    description: 'Total yellow and red cards shown across every game in this round.',
    metric: 'penalty_cards',
  },
  SOCCER_ROUND_RED_CARDS_OVER_UNDER: {
    label: 'Total Round Red Cards',
    description: 'Total red cards shown across every game in this round.',
    metric: 'red_cards',
  },
};

const PLAYER_VS_PLAYER_PROP_TYPES = [
  'PLAYER_VS_PLAYER_YARDS',
  'PLAYER_VS_PLAYER_RECEPTIONS',
  'PLAYER_VS_PLAYER_CARRIES',
];

const EXACTLY_TWO_PLAYER_PROP_TYPES = ['PLAYER_EITHER_REACHES_THRESHOLD'];
const MULTI_PLAYER_PROP_TYPES = ['ANYTIME_TD_MULTI'];
const FIRST_PLAYER_PROP_TYPES = ['FIRST_PLAYER_TO_STAT'];
const SOCCER_MULTI_PLAYER_PROP_TYPES = [
  'SOCCER_PLAYERS_COMBINED_OVER_UNDER',
  'SOCCER_ALL_PLAYERS_OVER_UNDER',
  'SOCCER_PLAYER_RACE_TO_STAT',
  'SOCCER_PLAYER_STAT_COMPARISON',
];
const SOCCER_TWO_PLAYER_PROP_TYPES = [...SOCCER_MULTI_PLAYER_PROP_TYPES, 'SOCCER_FIRST_GOAL_SCORER'];

const PROP_TYPE_DEFAULT_STAT_TYPE: Record<string, string> = {
  PLAYER_VS_PLAYER_RECEPTIONS: 'receptions',
  PLAYER_OVER_UNDER_RECEPTIONS: 'receptions',
  PLAYER_VS_PLAYER_CARRIES: 'rushing_attempts',
  PLAYER_OVER_UNDER_CARRIES: 'rushing_attempts',
  PLAYER_OVER_UNDER_INTERCEPTIONS: 'interceptions',
  PLAYER_OVER_UNDER_SACKS: 'sacks',
};

export type BetsCreatorTab = 'manage' | 'search' | 'resolution';
export type AutoPropSource = 'manual' | 'nfl' | 'soccer' | 'round' | 'kalshi';

export interface BetsCreatorInitialState {
  tab?: BetsCreatorTab;
  source?: AutoPropSource;
  searchTerm?: string;
  gameId?: string;
  propType?: string;
  statType?: string;
  threshold?: string;
  playerSearchTerm?: string;
  playerSearchFocused?: boolean;
}
type RoundPropMetric =
  | 'SOCCER_ROUND_EXTRA_TIME_OVER_UNDER'
  | 'SOCCER_ROUND_PENALTY_SHOOTOUT_OVER_UNDER'
  | 'SOCCER_ROUND_TOTAL_GOALS_OVER_UNDER'
  | 'SOCCER_ROUND_SCORELESS_REGULATION_TEAMS_OVER_UNDER'
  | 'SOCCER_ROUND_PENALTY_CARDS_OVER_UNDER'
  | 'SOCCER_ROUND_RED_CARDS_OVER_UNDER';

const parseOddsValue = (value: string) => {
  if (!value.trim()) {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return parsed;
};

const formatOddsInputValue = (value: number) => (value > 0 ? `+${value}` : String(value));

const parsePlayerIds = (value: string) =>
  value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);

const resolveWinningSideLabel = (bet: CustomPropBet): string => {
  const { winningSide, options, sideLabelOne, sideLabelTwo } = bet.displayInfo;
  if (!winningSide) return '';
  const sideKey = String(winningSide).trim();
  if (sideKey === 'push') return 'Push';
  
  if (options && Array.isArray(options)) {
    const match = options.find(o => String(o.side).trim() === sideKey);
    if (match) return match.label;
  }
  
  if (sideKey === 'one' || sideKey === '1') return sideLabelOne || 'Side 1';
  if (sideKey === 'two' || sideKey === '2') return sideLabelTwo || 'Side 2';
  
  return winningSide;
};

export default function BetsCreatorScreen({ initialState = {} }: { initialState?: BetsCreatorInitialState } = {}) {
  const { width: screenWidth } = useWindowDimensions();
  const isNarrowViewport = screenWidth < 600;
  const { user } = useAuth();
  const isBetsAdmin = Boolean(user?.isBetsAdmin);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const trpcClient = useTRPCClient();
  const { data: leagues } = useLeagues(isBetsAdmin);
  const theme = useTheme<Theme>();

  const [refreshing, setRefreshing] = useState(false);
  const [selectedWeekId, setSelectedWeekId] = useState<string | null>(null);
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [gameFilterId, setGameFilterId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState(initialState.searchTerm ?? '');
  const [searchQuery, setSearchQuery] = useState(initialState.searchTerm?.trim() ?? '');
  const searchInputRef = useRef<NativeTextInput | null>(null);
  const searchTypingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSearchTypingRef = useRef(false);
  const isSelectingPlayerRef = useRef(false);
  const playerSearchBlurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [editingBetId, setEditingBetId] = useState<string | null>(null);
  const [tabsDefault, setTabsDefault] = useState<BetsCreatorTab>(initialState.tab ?? 'manage');
  const [tabsKey, setTabsKey] = useState(0);
  const [showValidation, setShowValidation] = useState(false);
  const [enableModalVisible, setEnableModalVisible] = useState(false);
  const [enableModalBet, setEnableModalBet] = useState<CustomPropBetRecord | null>(null);
  const [enableModalChoice, setEnableModalChoice] = useState<'yes' | 'no' | 'propOfTheWeek'>('yes');
  const [enableLeagueId, setEnableLeagueId] = useState<string>('');
  const [overrideMode, setOverrideMode] = useState<'default' | 'multiplier' | 'fixed'>('default');
  const [overrideMultiplier, setOverrideMultiplier] = useState('1');
  const [overrideSideValues, setOverrideSideValues] = useState<Record<string, string>>({});
  const [isAutoProp, setIsAutoProp] = useState((initialState.source ?? 'manual') !== 'manual');
  const [autoPropSource, setAutoPropSource] = useState<AutoPropSource>(initialState.source ?? 'manual');
  const [isManualRoundProp, setIsManualRoundProp] = useState(false);
  const [kalshiUrl, setKalshiUrl] = useState('');
  const [autoUpdateKalshiOddsUntilKickoff, setAutoUpdateKalshiOddsUntilKickoff] = useState(false);
  const [playerSearchTerm, setPlayerSearchTerm] = useState(initialState.playerSearchTerm ?? '');
  const [playerSearchQuery, setPlayerSearchQuery] = useState(initialState.playerSearchTerm?.trim() ?? '');
  const [selectedPlayers, setSelectedPlayers] = useState<PlayerSearchResult[]>([]);
  const [isPlayerSearchFocused, setIsPlayerSearchFocused] = useState(initialState.playerSearchFocused ?? false);
  const autoPropSourceRef = useRef(autoPropSource);
  const playerSearchResetStateRef = useRef({
    gameId: initialState.gameId ?? '',
    propType: initialState.propType ?? '',
    isAutoProp: (initialState.source ?? 'manual') !== 'manual',
  });
  const [formState, setFormState] = useState<FormState>({
    gameId: initialState.gameId ?? '',
    label: '',
    description: '',
    // Deprecated but kept for compatibility logic if needed, 
    // though we will primarily use 'options' for UI state now.
    // We will sync options[0] and options[1] to these when submitting if we want to be safe, 
    // or just rely on the new API handling.
    // Actually, let's keep using options array in state.
    options: [
      { side: 'one', label: '', odds: '' },
      { side: 'two', label: '', odds: '' },
    ],
    winningSide: '',
    outcomeLabel: '',
    propType: initialState.propType ?? '',
    statType: initialState.statType ?? '',
    threshold: initialState.threshold ?? '',
    teamSide: '',
    playerIds: '',
  });

  const nflPropTypeOptions = useMemo(
    () => [
      { value: 'PLAYER_VS_PLAYER_YARDS', label: 'Player vs Player (Yards)' },
      { value: 'PLAYER_OVER_UNDER_YARDS', label: 'Player Over/Under (Yards)' },
      { value: 'PLAYER_EITHER_REACHES_THRESHOLD', label: 'Either Player Reaches a Stat Line' },
      { value: 'PLAYER_VS_PLAYER_RECEPTIONS', label: 'Player vs Player (Receptions)' },
      { value: 'PLAYER_OVER_UNDER_RECEPTIONS', label: 'Player Over/Under (Receptions)' },
      { value: 'PLAYER_VS_PLAYER_CARRIES', label: 'Player vs Player (Carries)' },
      { value: 'PLAYER_OVER_UNDER_CARRIES', label: 'Player Over/Under (Carries)' },
      { value: 'PLAYER_OVER_UNDER_INTERCEPTIONS', label: 'Player Over/Under (Interceptions)' },
      { value: 'PLAYER_OVER_UNDER_SACKS', label: 'Player Over/Under (Sacks)' },
      { value: 'ANYTIME_TD', label: 'Anytime TD' },
      { value: 'RUSHING_TD', label: 'Rushing TD' },
      { value: 'RECEIVING_TD', label: 'Receiving TD' },
      { value: 'PASSING_TD', label: 'Passing TD' },
      { value: 'ANYTIME_TD_MULTI', label: 'Any of These Players Score a TD' },
      { value: 'FIRST_PLAYER_TO_STAT', label: 'First Player to Score a TD' },
      { value: 'LONGEST_STAT', label: 'Longest Stat' },
    ],
    []
  );

  const soccerPropTypeOptions = useMemo(
    () => [
      { value: 'SOCCER_BOTH_TEAMS_TO_SCORE', label: 'Both Teams To Score' },
      { value: 'SOCCER_PLAYER_OVER_UNDER', label: 'Player Over/Under' },
      { value: 'SOCCER_PLAYER_EACH_HALF_OVER_UNDER', label: 'Player Each Half Over/Under' },
      { value: 'SOCCER_PLAYERS_COMBINED_OVER_UNDER', label: 'Either Player Combined Over/Under' },
      { value: 'SOCCER_ALL_PLAYERS_OVER_UNDER', label: 'All Players Over/Under' },
      { value: 'SOCCER_PLAYER_RACE_TO_STAT', label: 'Player Race to Stat' },
      { value: 'SOCCER_TEAM_RACE_TO_STAT', label: 'Team Race to Stat' },
      { value: 'SOCCER_BOTH_TEAMS_OVER_STAT', label: 'Both Teams Over Stat' },
      { value: 'SOCCER_PLAYER_STAT_COMPARISON', label: 'Player Stat Comparison' },
      { value: 'SOCCER_TEAM_STAT_COMPARISON', label: 'Team Stat Comparison' },
      { value: 'SOCCER_CORNERS_OVER_UNDER', label: 'Corner Kicks Over/Under' },
      { value: 'SOCCER_FIRST_TEAM_TO_CORNERS', label: 'Race to X Corner Kicks' },
      { value: 'SOCCER_FOULS_OVER_UNDER', label: 'Fouls Over/Under' },
      { value: 'SOCCER_TEAM_FOULS_OVER_UNDER', label: 'Team Fouls Over/Under' },
      { value: 'SOCCER_OFFSIDES_OVER_UNDER', label: 'Offsides Over/Under' },
      { value: 'SOCCER_TEAM_OFFSIDES_OVER_UNDER', label: 'Team Offsides Over/Under' },
      { value: 'SOCCER_TEAM_GOALS_ALLOWED_OVER_UNDER', label: 'Team Goals Allowed Over/Under' },
      { value: 'SOCCER_TEAM_SHOTS_ON_TARGET_PCT_OVER_UNDER', label: 'Team Shots on Target % Over/Under' },
      { value: 'SOCCER_GOALS_ODD_EVEN', label: 'Goals Odd/Even' },
      { value: 'SOCCER_METHOD_OF_VICTORY', label: 'Method of Victory' },
      { value: 'SOCCER_FIRST_GOAL_INTERVAL', label: 'First Goal Interval' },
      { value: 'SOCCER_FIRST_GOAL_METHOD', label: 'First Goal Method' },
      { value: 'SOCCER_FIRST_GOAL_SCORER', label: 'First Goal Scorer' },
      { value: 'SOCCER_ROUND_EXTRA_TIME_OVER_UNDER', label: 'Round: Games To Extra Time' },
      { value: 'SOCCER_ROUND_PENALTY_SHOOTOUT_OVER_UNDER', label: 'Round: Games To Penalty Kicks' },
      { value: 'SOCCER_ROUND_TOTAL_GOALS_OVER_UNDER', label: 'Round: Total Goals' },
      { value: 'SOCCER_ROUND_SCORELESS_REGULATION_TEAMS_OVER_UNDER', label: 'Round: Scoreless Regulation Teams' },
      { value: 'SOCCER_ROUND_PENALTY_CARDS_OVER_UNDER', label: 'Round: Penalty Cards' },
      { value: 'SOCCER_ROUND_RED_CARDS_OVER_UNDER', label: 'Round: Red Cards' },
    ],
    []
  );

  const roundPropTypeOptions = useMemo(
    () => [
      { value: 'SOCCER_ROUND_EXTRA_TIME_OVER_UNDER', label: 'Over/Under Games To Extra Time' },
      { value: 'SOCCER_ROUND_PENALTY_SHOOTOUT_OVER_UNDER', label: 'Over/Under Games To Penalty Kicks' },
      { value: 'SOCCER_ROUND_TOTAL_GOALS_OVER_UNDER', label: 'Over/Under Total Goals This Round' },
      { value: 'SOCCER_ROUND_SCORELESS_REGULATION_TEAMS_OVER_UNDER', label: 'Over/Under Scoreless Regulation Teams' },
      { value: 'SOCCER_ROUND_PENALTY_CARDS_OVER_UNDER', label: 'Over/Under Penalty Cards This Round' },
      { value: 'SOCCER_ROUND_RED_CARDS_OVER_UNDER', label: 'Over/Under Red Cards This Round' },
    ],
    []
  );

  const propTypeOptions =
    autoPropSource === 'round'
      ? roundPropTypeOptions
      : autoPropSource === 'soccer'
        ? soccerPropTypeOptions
        : nflPropTypeOptions;

  const nflStatTypeOptions = useMemo(
    () => [
      { value: 'passing_yards', label: 'Passing Yards' },
      { value: 'rushing_yards', label: 'Rushing Yards' },
      { value: 'receptions_yards', label: 'Receiving Yards' },
      { value: 'rushing_receiving_yards', label: 'Rushing + Receiving Yards' },
      { value: 'passing_long', label: 'Longest Pass' },
      { value: 'rushing_long', label: 'Longest Rush' },
      { value: 'receptions_long', label: 'Longest Reception' },
      { value: 'touchdowns', label: 'Touchdowns' },
    ],
    []
  );

  const soccerStatTypeOptions = useMemo(
    () => [
      { value: 'soccer_goals', label: 'Goals' },
      { value: 'soccer_assists', label: 'Assists' },
      { value: 'soccer_goals_assists', label: 'Goals + Assists' },
      { value: 'soccer_saves', label: 'Saves' },
      { value: 'soccer_shots', label: 'Shots' },
      { value: 'soccer_shots_on_target', label: 'Shots on Target' },
      { value: 'soccer_shots_on_target_pct', label: 'Shots on Target %' },
      { value: 'soccer_corners', label: 'Corner Kicks' },
    ],
    []
  );

  const statTypeOptions =
    autoPropSource === 'soccer'
      ? soccerStatTypeOptions
      : formState.propType.trim() === 'FIRST_PLAYER_TO_STAT'
        ? nflStatTypeOptions.filter((option) => option.value === 'touchdowns')
        : nflStatTypeOptions;

  const logLayout =
    (name: string) =>
    (event: { nativeEvent: { layout: { x: number; y: number; width: number; height: number } } }) => {
      const { x, y, width, height } = event.nativeEvent.layout;
      clientLogger.debug(`[layout] ${name}`, { x, y, width, height });
    };

  useFocusEffect(
    useCallback(() => {
      if (!isBetsAdmin) {
        return;
      }
      queryClient.invalidateQueries(trpc.nfl.weeks.queryFilter());
      queryClient.invalidateQueries(trpc.nfl.currentWeek.queryFilter());
    }, [isBetsAdmin, queryClient])
  );

  const { data: weeks } = useQuery(
    trpc.nfl.weeks.queryOptions(undefined, {
      enabled: isBetsAdmin,
    }),
  );

  const { data: currentWeek } = useQuery(
    trpc.nfl.currentWeek.queryOptions(undefined, {
      enabled: isBetsAdmin,
    }),
  );

  useEffect(() => {
    if (!isBetsAdmin) return;
    const availableWeeks = weeks ?? [];
    if (availableWeeks.length === 0) {
      setSelectedWeekId(null);
      return;
    }

    const fallbackWeekId = availableWeeks[0]?.id;
    if (!fallbackWeekId) {
      setSelectedWeekId(null);
      return;
    }

    const selectionExists = availableWeeks.some((week) => week.id === selectedWeekId);
    if (!selectedWeekId || !selectionExists) {
      const activeWeek = availableWeeks.find((week) => week.isActive);
      setSelectedWeekId(activeWeek?.id ?? fallbackWeekId);
    }
  }, [isBetsAdmin, selectedWeekId, weeks]);

  useEffect(() => {
    if (!isBetsAdmin) return;
    const availableLeagues = leagues ?? [];
    if (availableLeagues.length === 0) {
      setSelectedLeagueId('');
      return;
    }

    const defaultLeagueId = user?.defaultLeagueId ? String(user.defaultLeagueId) : null;
    const fallbackLeagueId = defaultLeagueId ?? String(availableLeagues[0]?.id ?? '');
    const selectionExists = availableLeagues.some((league) => String(league.id) === selectedLeagueId);
    if (!selectedLeagueId || !selectionExists) {
      setSelectedLeagueId(fallbackLeagueId);
    }
  }, [isBetsAdmin, leagues, selectedLeagueId, user?.defaultLeagueId]);

  useEffect(() => {
    if (!enableModalVisible) {
      return;
    }
    const availableLeagues = leagues ?? [];
    const defaultLeagueId = user?.defaultLeagueId ? String(user.defaultLeagueId) : null;
    const fallbackLeagueId = defaultLeagueId ?? String(availableLeagues[0]?.id ?? '');
    setEnableLeagueId(fallbackLeagueId);
    setEnableModalChoice(availableLeagues.length > 0 ? 'yes' : 'no');
    setOverrideMode('default');
    setOverrideMultiplier('1');
    setOverrideSideValues({});
  }, [enableModalVisible, leagues, user?.defaultLeagueId]);

  const { data: games } = useQuery(
    trpc.nfl.gamesByWeek.queryOptions(
      { weekId: selectedWeekId ?? '' },
      {
        enabled: isBetsAdmin && !!selectedWeekId,
      },
    ),
  );

  const selectedGame = useMemo(
    () => (games ?? []).find((game) => game.id === formState.gameId) ?? null,
    [formState.gameId, games]
  );
  const manualRoundGameIds = useMemo(() => (games ?? []).map((game) => game.id).filter(Boolean), [games]);
  const selectedGameTeamAbbrs = useMemo(() => {
    const homeAbbr = selectedGame?.homeTeam?.abbreviation;
    const awayAbbr = selectedGame?.awayTeam?.abbreviation;
    return [homeAbbr, awayAbbr].filter((abbr): abbr is string => Boolean(abbr));
  }, [selectedGame]);
  const soccerTeamSideOptions = useMemo(
    () => {
      const propType = formState.propType.trim();
      const suffix =
        propType === 'SOCCER_TEAM_GOALS_ALLOWED_OVER_UNDER'
          ? 'goals allowed'
          : propType === 'SOCCER_TEAM_SHOTS_ON_TARGET_PCT_OVER_UNDER'
            ? 'shots on target %'
            : propType === 'SOCCER_GOALS_ODD_EVEN'
              ? 'goals'
            : 'fouls';
      const teamOptions = [
        ...(propType === 'SOCCER_GOALS_ODD_EVEN'
          ? [
              {
                value: '',
                label: 'Match total goals',
              },
            ]
          : []),
        {
          value: 'home',
          label: selectedGame?.homeTeam
            ? `${selectedGame.homeTeam.abbreviation || selectedGame.homeTeam.name} ${suffix}`
            : `Home team ${suffix}`,
        },
        {
          value: 'away',
          label: selectedGame?.awayTeam
            ? `${selectedGame.awayTeam.abbreviation || selectedGame.awayTeam.name} ${suffix}`
            : `Away team ${suffix}`,
        },
      ];
      return [
        ...teamOptions,
      ];
    },
    [formState.propType, selectedGame]
  );
  const canSetOutcome = useMemo(() => {
    if (!selectedGame?.gameTime) {
      return false;
    }
    const startTime = new Date(selectedGame.gameTime);
    if (Number.isNaN(startTime.getTime())) {
      return false;
    }
    return startTime <= getCurrentTime();
  }, [selectedGame]);
  const isRoundProp = autoPropSource === 'round' || ROUND_PROP_TYPES.includes(formState.propType.trim() as RoundPropMetric);
  const isGameLevelSoccerProp = isRoundProp || GAME_LEVEL_SOCCER_PROP_TYPES.includes(formState.propType.trim());

  useEffect(() => {
    if (!canSetOutcome && (formState.winningSide || formState.outcomeLabel)) {
      setFormState((prev) => ({
        ...prev,
        winningSide: '',
        outcomeLabel: '',
      }));
    }
  }, [canSetOutcome, formState.outcomeLabel, formState.winningSide]);

  useEffect(() => {
    if (!gameFilterId) {
      return;
    }
    const availableGames = games ?? [];
    const exists = availableGames.some((game) => game.id === gameFilterId);
    if (!exists) {
      setGameFilterId('');
    }
  }, [games, gameFilterId]);

  useEffect(() => {
    const trimmed = searchTerm.trim();
    const timeout = setTimeout(() => {
      setSearchQuery(trimmed);
    }, 250);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    if (!isAutoProp) {
      setPlayerSearchQuery('');
      setPlayerSearchTerm('');
      setSelectedPlayers([]);
      return;
    }
    const trimmed = playerSearchTerm.trim();
    const timeout = setTimeout(() => {
      setPlayerSearchQuery(trimmed);
    }, 250);
    return () => clearTimeout(timeout);
  }, [isAutoProp, playerSearchTerm]);

  useEffect(() => {
    setIsAutoProp(autoPropSource !== 'manual');
    if (autoPropSource !== 'kalshi') {
      setAutoUpdateKalshiOddsUntilKickoff(false);
    }
    if (autoPropSourceRef.current === autoPropSource) {
      return;
    }
    autoPropSourceRef.current = autoPropSource;
    if (autoPropSource !== 'manual') {
      setIsManualRoundProp(false);
    }
    if (autoPropSource === 'manual') {
      setFormState((prev) => ({
        ...prev,
        propType: '',
        statType: '',
        threshold: '',
        teamSide: '',
        playerIds: '',
      }));
      return;
    }

    setFormState((prev) => ({
      ...prev,
      propType: '',
      statType: '',
      threshold: '',
      teamSide: '',
      playerIds: '',
      options:
        autoPropSource === 'soccer' || autoPropSource === 'round' || autoPropSource === 'kalshi'
          ? [
              { side: 'one', label: autoPropSource === 'round' ? 'Over' : 'Yes', odds: '+100' },
              { side: 'two', label: autoPropSource === 'round' ? 'Under' : 'No', odds: '+100' },
            ]
          : [
              { side: 'one', label: '', odds: '' },
              { side: 'two', label: '', odds: '' },
            ],
    }));
  }, [autoPropSource]);

  useEffect(() => {
    const propType = formState.propType.trim() as RoundPropMetric;
    const defaults = ROUND_PROP_LABELS[propType];
    if (!defaults) {
      return;
    }
    setFormState((prev) => ({
      ...prev,
      label: defaults.label,
      description: defaults.description,
      statType: '',
      teamSide: '',
      playerIds: '',
      options: [
        { side: 'one', label: 'Over', odds: prev.options[0]?.odds || '+100' },
        { side: 'two', label: 'Under', odds: prev.options[1]?.odds || '+100' },
      ],
    }));
  }, [autoPropSource, formState.propType]);

  useEffect(() => {
    if (autoPropSource !== 'nfl' || formState.propType.trim() !== 'PLAYER_EITHER_REACHES_THRESHOLD') {
      return;
    }
    setFormState((prev) => ({
      ...prev,
      label: prev.label || 'Either Player Reaches a Stat Line',
      description: prev.description || 'Yes wins if either selected player reaches the stat line.',
      options: [
        { side: 'one', label: 'Yes', odds: prev.options[0]?.odds || '+100' },
        { side: 'two', label: 'No', odds: prev.options[1]?.odds || '+100' },
      ],
    }));
  }, [autoPropSource, formState.propType]);

  useEffect(() => {
    if (autoPropSource !== 'soccer') {
      return;
    }

    setFormState((prev) => {
      if (prev.propType === 'SOCCER_BOTH_TEAMS_TO_SCORE') {
        return {
          ...prev,
          label: prev.label || 'Both Teams To Score',
          description: prev.description || 'Will both teams score at least one goal?',
          statType: '',
          threshold: '',
          playerIds: '',
          options: [
            { side: 'one', label: 'Yes', odds: prev.options[0]?.odds || '+100' },
            { side: 'two', label: 'No', odds: prev.options[1]?.odds || '+100' },
          ],
        };
      }

      if (prev.propType === 'SOCCER_CORNERS_OVER_UNDER') {
        return {
          ...prev,
          label: prev.label || 'Total Corner Kicks',
          description: prev.description || 'Total corner kicks by both teams.',
          statType: '',
          playerIds: '',
          options: [
            { side: 'one', label: 'Over', odds: prev.options[0]?.odds || '+100' },
            { side: 'two', label: 'Under', odds: prev.options[1]?.odds || '+100' },
          ],
        };
      }

      if (prev.propType === 'SOCCER_FIRST_TEAM_TO_CORNERS') {
        return {
          ...prev,
          label: prev.label || 'Race to Corners',
          description: prev.description || 'Which team will be first to the selected corner kick target?',
          statType: '',
          playerIds: '',
          options: [
            { side: 'one', label: 'Home', odds: prev.options[0]?.odds || '+100' },
            { side: 'two', label: 'Away', odds: prev.options[1]?.odds || '+100' },
            { side: 'three', label: 'Neither', odds: prev.options[2]?.odds || '+100' },
          ],
        };
      }

      if (prev.propType === 'SOCCER_FOULS_OVER_UNDER') {
        return {
          ...prev,
          label: prev.label || 'Total Fouls',
          description: prev.description || 'Total fouls committed by both teams.',
          statType: '',
          playerIds: '',
          options: [
            { side: 'one', label: 'Over', odds: prev.options[0]?.odds || '+100' },
            { side: 'two', label: 'Under', odds: prev.options[1]?.odds || '+100' },
          ],
        };
      }

      if (prev.propType === 'SOCCER_TEAM_FOULS_OVER_UNDER') {
        return {
          ...prev,
          label: prev.label || 'Team Fouls',
          description: prev.description || 'Fouls committed by the selected team.',
          statType: '',
          playerIds: '',
          teamSide: prev.teamSide || 'home',
          options: [
            { side: 'one', label: 'Over', odds: prev.options[0]?.odds || '+100' },
            { side: 'two', label: 'Under', odds: prev.options[1]?.odds || '+100' },
          ],
        };
      }

      if (prev.propType === 'SOCCER_OFFSIDES_OVER_UNDER') {
        return {
          ...prev,
          label: prev.label || 'Total Offsides',
          description: prev.description || 'Total offsides by both teams.',
          statType: '',
          playerIds: '',
          options: [
            { side: 'one', label: 'Over', odds: prev.options[0]?.odds || '+100' },
            { side: 'two', label: 'Under', odds: prev.options[1]?.odds || '+100' },
          ],
        };
      }

      if (prev.propType === 'SOCCER_TEAM_OFFSIDES_OVER_UNDER') {
        return {
          ...prev,
          label: prev.label || 'Team Offsides',
          description: prev.description || 'Offsides by the selected team.',
          statType: '',
          playerIds: '',
          teamSide: prev.teamSide || 'home',
          options: [
            { side: 'one', label: 'Over', odds: prev.options[0]?.odds || '+100' },
            { side: 'two', label: 'Under', odds: prev.options[1]?.odds || '+100' },
          ],
        };
      }

      if (prev.propType === 'SOCCER_TEAM_GOALS_ALLOWED_OVER_UNDER') {
        return {
          ...prev,
          label: prev.label || 'Team Goals Allowed',
          description: prev.description || 'Goals conceded by the selected team.',
          statType: '',
          playerIds: '',
          teamSide: prev.teamSide || 'home',
          options: [
            { side: 'one', label: 'Over', odds: prev.options[0]?.odds || '+100' },
            { side: 'two', label: 'Under', odds: prev.options[1]?.odds || '+100' },
          ],
        };
      }

      if (prev.propType === 'SOCCER_TEAM_SHOTS_ON_TARGET_PCT_OVER_UNDER') {
        return {
          ...prev,
          label: prev.label || 'Team Shots on Target %',
          description: prev.description || 'Selected team shots on target divided by total shots.',
          statType: '',
          playerIds: '',
          teamSide: prev.teamSide || 'home',
          options: [
            { side: 'one', label: 'Over', odds: prev.options[0]?.odds || '+100' },
            { side: 'two', label: 'Under', odds: prev.options[1]?.odds || '+100' },
          ],
        };
      }

      if (prev.propType === 'SOCCER_GOALS_ODD_EVEN') {
        return {
          ...prev,
          label: prev.label || 'Goals Odd/Even',
          description: prev.description || 'Will the selected goal total finish odd or even?',
          statType: '',
          threshold: '',
          playerIds: '',
          teamSide: prev.teamSide === 'home' || prev.teamSide === 'away' ? prev.teamSide : '',
          options: [
            { side: 'one', label: 'Odd', odds: prev.options[0]?.odds || '+100' },
            { side: 'two', label: 'Even', odds: prev.options[1]?.odds || '+100' },
          ],
        };
      }

      if (prev.propType === 'SOCCER_METHOD_OF_VICTORY') {
        return {
          ...prev,
          label: prev.label || 'Method of Victory',
          description: prev.description || 'When will this match winner be decided?',
          statType: '',
          threshold: '',
          playerIds: '',
          teamSide: '',
          options: [
            { side: 'one', label: '90 minutes', odds: prev.options[0]?.odds || '+100' },
            { side: 'two', label: 'Extra time', odds: prev.options[1]?.odds || '+100' },
            { side: 'three', label: 'Penalties', odds: prev.options[2]?.odds || '+100' },
          ],
        };
      }

      if (prev.propType === 'SOCCER_FIRST_GOAL_METHOD') {
        return {
          ...prev,
          label: prev.label || 'First Goal Method',
          description: prev.description || 'How will the first goal of the match be scored?',
          statType: '',
          threshold: '',
          playerIds: '',
          teamSide: '',
          options: [
            { side: 'one', label: 'Shot', odds: prev.options[0]?.odds || '+100' },
            { side: 'two', label: 'Header', odds: prev.options[1]?.odds || '+100' },
            { side: 'three', label: 'Penalty', odds: prev.options[2]?.odds || '+100' },
            { side: 'four', label: 'Own Goal', odds: prev.options[3]?.odds || '+100' },
            { side: 'five', label: 'No Goal', odds: prev.options[4]?.odds || '+100' },
            { side: 'six', label: 'Free Kick', odds: prev.options[5]?.odds || '+100' },
          ],
        };
      }

      if (prev.propType === 'SOCCER_FIRST_GOAL_INTERVAL') {
        return {
          ...prev,
          label: prev.label || 'First Goal Interval',
          description: prev.description || 'When will the first goal of the match be scored?',
          statType: '',
          threshold: '',
          playerIds: '',
          teamSide: '',
          options: [
            { side: 'one', label: '1-10 Min', odds: prev.options[0]?.odds || '+100' },
            { side: 'two', label: '11-20 Min', odds: prev.options[1]?.odds || '+100' },
            { side: 'three', label: '21-30 Min', odds: prev.options[2]?.odds || '+100' },
            { side: 'four', label: '31-40 Min', odds: prev.options[3]?.odds || '+100' },
            { side: 'five', label: '41-50 Min', odds: prev.options[4]?.odds || '+100' },
            { side: 'six', label: '51-60 Min', odds: prev.options[5]?.odds || '+100' },
            { side: 'seven', label: '61-70 Min', odds: prev.options[6]?.odds || '+100' },
            { side: 'eight', label: '71-80 Min', odds: prev.options[7]?.odds || '+100' },
            { side: 'nine', label: '81-90 Min', odds: prev.options[8]?.odds || '+100' },
            { side: 'ten', label: 'Stoppage Time', odds: prev.options[9]?.odds || '+100' },
            { side: 'eleven', label: 'No Goal in Regulation', odds: prev.options[10]?.odds || '+100' },
          ],
        };
      }

      if (prev.propType === 'SOCCER_FIRST_GOAL_SCORER') {
        return {
          ...prev,
          label: prev.label || 'First Goal Scorer',
          description: prev.description || 'Which selected player will score the first goal of the match?',
          statType: '',
          threshold: '',
          teamSide: '',
          options: [
            { side: 'one', label: 'Player 1', odds: prev.options[0]?.odds || '+100' },
            { side: 'two', label: 'Player 2', odds: prev.options[1]?.odds || '+100' },
            { side: 'three', label: 'Neither', odds: prev.options[2]?.odds || '+100' },
          ],
        };
      }

      if (prev.propType === 'SOCCER_PLAYER_OVER_UNDER') {
        return {
          ...prev,
          label: prev.label || 'Player Prop Over/Under',
          description: prev.description || 'Player stat total for this World Cup match.',
          options: [
            { side: 'one', label: 'Over', odds: prev.options[0]?.odds || '+100' },
            { side: 'two', label: 'Under', odds: prev.options[1]?.odds || '+100' },
          ],
        };
      }

      if (prev.propType === 'SOCCER_PLAYER_EACH_HALF_OVER_UNDER') {
        return {
          ...prev,
          label: prev.label || 'Player Each Half Over/Under',
          description: prev.description || 'Player must clear the selected stat line in each half.',
          options: [
            { side: 'one', label: 'Over in each half', odds: prev.options[0]?.odds || '+100' },
            { side: 'two', label: 'Under in either half', odds: prev.options[1]?.odds || '+100' },
          ],
        };
      }

      if (prev.propType === 'SOCCER_PLAYER_STAT_COMPARISON') {
        return {
          ...prev,
          label: prev.label || 'Player Stat Comparison',
          description: prev.description || 'Which selected player will finish with more of the selected stat?',
          threshold: '',
          teamSide: '',
          options: [
            { side: 'one', label: 'Player 1', odds: prev.options[0]?.odds || '+100' },
            { side: 'two', label: 'Player 2', odds: prev.options[1]?.odds || '+100' },
          ],
        };
      }

      if (prev.propType === 'SOCCER_PLAYER_RACE_TO_STAT') {
        return {
          ...prev,
          label: prev.label || 'Player Race to Stat',
          description: prev.description || 'Which selected player will be first to the stat target?',
          teamSide: '',
          options: [
            { side: 'one', label: 'Player 1', odds: prev.options[0]?.odds || '+100' },
            { side: 'two', label: 'Player 2', odds: prev.options[1]?.odds || '+100' },
            { side: 'three', label: 'Neither', odds: prev.options[2]?.odds || '+100' },
          ],
        };
      }

      if (prev.propType === 'SOCCER_TEAM_STAT_COMPARISON') {
        return {
          ...prev,
          label: prev.label || 'Team Stat Comparison',
          description: prev.description || 'Which team will finish with more of the selected stat?',
          threshold: '',
          playerIds: '',
          teamSide: '',
          options: [
            { side: 'one', label: 'Home', odds: prev.options[0]?.odds || '+100' },
            { side: 'two', label: 'Away', odds: prev.options[1]?.odds || '+100' },
          ],
        };
      }

      if (prev.propType === 'SOCCER_TEAM_RACE_TO_STAT') {
        return {
          ...prev,
          label: prev.label || 'Team Race to Stat',
          description: prev.description || 'Which team will be first to the selected stat target?',
          playerIds: '',
          teamSide: '',
          options: [
            { side: 'one', label: 'Home', odds: prev.options[0]?.odds || '+100' },
            { side: 'two', label: 'Away', odds: prev.options[1]?.odds || '+100' },
            { side: 'three', label: 'Neither', odds: prev.options[2]?.odds || '+100' },
          ],
        };
      }

      if (prev.propType === 'SOCCER_BOTH_TEAMS_OVER_STAT') {
        return {
          ...prev,
          label: prev.label || 'Both Teams Over Stat',
          description: prev.description || 'Both teams must finish over the selected stat threshold.',
          playerIds: '',
          teamSide: '',
          options: [
            { side: 'one', label: 'Yes', odds: prev.options[0]?.odds || '+100' },
            { side: 'two', label: 'No', odds: prev.options[1]?.odds || '+100' },
          ],
        };
      }

      if (prev.propType === 'SOCCER_PLAYERS_COMBINED_OVER_UNDER') {
        return {
          ...prev,
          label: prev.label || 'Either Player Combined Over/Under',
          description: prev.description || 'Combined stat total for selected World Cup players.',
          options: [
            { side: 'one', label: 'Over', odds: prev.options[0]?.odds || '+100' },
            { side: 'two', label: 'Under', odds: prev.options[1]?.odds || '+100' },
          ],
        };
      }

      if (prev.propType === 'SOCCER_ALL_PLAYERS_OVER_UNDER') {
        return {
          ...prev,
          label: prev.label || 'All Players Over/Under',
          description: prev.description || 'Every selected World Cup player must clear the selected stat line.',
          options: [
            { side: 'one', label: 'All Over', odds: prev.options[0]?.odds || '+100' },
            { side: 'two', label: 'Any Under', odds: prev.options[1]?.odds || '+100' },
          ],
        };
      }

      return prev;
    });
  }, [autoPropSource, formState.propType]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    isSearchTypingRef.current = true;
    if (searchTypingTimeoutRef.current) {
      clearTimeout(searchTypingTimeoutRef.current);
    }
    searchTypingTimeoutRef.current = setTimeout(() => {
      isSearchTypingRef.current = false;
    }, 1000);
  };

  const betListFilters = useMemo(
    () => ({
      ...(selectedWeekId ? { weekId: selectedWeekId } : {}),
      ...(gameFilterId ? { gameId: gameFilterId } : {}),
      ...(searchQuery ? { search: searchQuery } : {}),
    }),
    [gameFilterId, searchQuery, selectedWeekId],
  );

  const customPropsQuery = useQuery(
    trpc.nfl.customProps.queryOptions(betListFilters, {
      enabled: isBetsAdmin && !!selectedWeekId,
      placeholderData: keepPreviousData,
    }),
  );
  const customProps = customPropsQuery.data as CustomPropBet[] | undefined;
  const customPropsLoading = customPropsQuery.isLoading;

  const { data: propDefinitions, isLoading: propDefinitionsLoading } = useQuery(
    trpc.nfl.propDefinitions.queryOptions(betListFilters, {
      enabled: isBetsAdmin && !!selectedWeekId,
      placeholderData: keepPreviousData,
    }),
  );

  const propDefinitionIds = useMemo(
    () => (propDefinitions ?? []).map((definition) => definition.id),
    [propDefinitions]
  );

  const onRefresh = useCallback(async () => {
    if (!isBetsAdmin) return;
    setRefreshing(true);
    await Promise.all([
      queryClient.invalidateQueries(trpc.nfl.weeks.queryFilter()),
      queryClient.invalidateQueries(trpc.nfl.currentWeek.queryFilter()),
      queryClient.invalidateQueries(trpc.nfl.propDefinitions.queryFilter()),
      queryClient.invalidateQueries(trpc.nfl.propBetsByDefinition.queryFilter()),
      queryClient.invalidateQueries(trpc.nfl.customProps.queryFilter()),
    ]);
    setRefreshing(false);
  }, [isBetsAdmin, queryClient]);

  const { data: propDefinitionBets, isLoading: propDefinitionBetsLoading } = useQuery(
    trpc.nfl.propBetsByDefinition.queryOptions(
      { propDefinitionIds },
      {
        enabled: isBetsAdmin && propDefinitionIds.length > 0,
        placeholderData: keepPreviousData,
      },
    ),
  );

  const { data: playerSearchResults, isLoading: playerSearchLoading } = useQuery(
    trpc.nfl.searchPlayers.queryOptions(
      {
        query: playerSearchQuery.trim() || ' ',
        limit: 8,
        ...(selectedGameTeamAbbrs.length > 0 ? { teamAbbrs: selectedGameTeamAbbrs } : {}),
      },
      {
        enabled:
          isBetsAdmin &&
          isAutoProp &&
          !isGameLevelSoccerProp &&
          isPlayerSearchFocused &&
          playerSearchQuery.trim().length >= 2 &&
          selectedGameTeamAbbrs.length > 0,
        placeholderData: keepPreviousData,
      },
    ),
  );

  const autoPropsLoading = propDefinitionsLoading || propDefinitionBetsLoading;
  const availablePlayerResults = useMemo(
    () =>
      (playerSearchResults ?? []).filter(
        (player) => !selectedPlayers.some((selected) => selected.id === player.id)
      ),
    [playerSearchResults, selectedPlayers]
  );
  const showPlayerResults =
    isAutoProp &&
    !isGameLevelSoccerProp &&
    !(EXACTLY_TWO_PLAYER_PROP_TYPES.includes(formState.propType.trim()) && selectedPlayers.length >= 2) &&
    isPlayerSearchFocused &&
    (playerSearchQuery.trim().length >= 2 || playerSearchLoading);

  useEffect(() => {
    if (!isAutoProp) {
      return;
    }
    const ids = selectedPlayers.map((player) => player.id).join(', ');
    setFormState((prev) => ({
      ...prev,
      playerIds: ids,
    }));
  }, [isAutoProp, selectedPlayers]);

  useEffect(() => {
    if (autoPropSource !== 'nfl' || formState.propType.trim() !== 'FIRST_PLAYER_TO_STAT') {
      return;
    }

    setFormState((prev) => {
      const previousByPlayerId = new Map(
        prev.options
          .filter((option) => option.playerId)
          .map((option) => [option.playerId, option]),
      );
      const previousNoneOption = prev.options.find((option) => option.outcome === 'none')
        ?? prev.options[selectedPlayers.length];

      if (selectedPlayers.length < 2) {
        return {
          ...prev,
          label: prev.label || 'First Player to Score a TD',
          description: prev.description || 'Which selected player will score the first touchdown of the game?',
          statType: 'touchdowns',
          threshold: '',
        };
      }

      const playerOptions = selectedPlayers.map((player, index) => {
        const previous = previousByPlayerId.get(player.id) ?? prev.options[index];
        return {
          side: PROP_SIDE_KEYS[index] ?? `side-${index + 1}`,
          label: player.fullName,
          odds: previous?.odds || '+100',
          playerId: player.id,
          outcome: 'player' as const,
        };
      });
      const noneSide = PROP_SIDE_KEYS[selectedPlayers.length];
      if (!noneSide) {
        return prev;
      }

      return {
        ...prev,
        label: prev.label || 'First Player to Score a TD',
        description: prev.description || 'Which selected player will score the first touchdown of the game?',
        statType: 'touchdowns',
        threshold: '',
        teamSide: '',
        options: [
          ...playerOptions,
          {
            side: noneSide,
            label: 'Neither',
            odds: previousNoneOption?.odds || '+100',
            outcome: 'none' as const,
          },
        ],
      };
    });
  }, [autoPropSource, formState.propType, selectedPlayers]);

  useEffect(() => {
    const previous = playerSearchResetStateRef.current;
    const current = {
      gameId: formState.gameId,
      propType: formState.propType,
      isAutoProp,
    };
    playerSearchResetStateRef.current = current;
    if (
      previous.gameId === current.gameId &&
      previous.propType === current.propType &&
      previous.isAutoProp === current.isAutoProp
    ) {
      return;
    }
    if (!isAutoProp) {
      return;
    }
    setSelectedPlayers([]);
    setPlayerSearchTerm('');
    setPlayerSearchQuery('');
  }, [formState.gameId, formState.propType, isAutoProp]);

  useEffect(() => {
    if (!isSearchTypingRef.current || !searchTerm.trim()) {
      return;
    }
    const timeout = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
    return () => clearTimeout(timeout);
  }, [customProps, searchTerm]);

  const gameById = useMemo(() => {
    const entries: readonly [string, NflGame][] = (games ?? []).map((game) => [game.id, game] as const);
    return new Map(entries);
  }, [games]);

  const autoPropBets: CustomPropBet[] = useMemo(() => {
    if (!propDefinitionBets || !propDefinitions) {
      return [];
    }
    const definitionsById = new Map(propDefinitions.map((definition) => [definition.id, definition]));
    const results: CustomPropBet[] = [];

    for (const bet of propDefinitionBets) {
      const game = gameById.get(bet.nflGameId);
      if (!game) {
        continue;
      }
      const displayInfo =
        bet.displayInfo ??
        (definitionsById.get(bet.propDefinitionId ?? '')?.displayConfig ?? {});

      results.push({
        id: bet.id,
        nflGameId: bet.nflGameId,
        propDefinitionId: bet.propDefinitionId ?? null,
        nflBetType: 'custom_prop',
        oddsOne: bet.oddsOne ?? null,
        oddsTwo: bet.oddsTwo ?? null,
        displayInfo: displayInfo as CustomPropDisplayInfo,
        createdAt: bet.createdAt ?? '',
        updatedAt: bet.updatedAt ?? '',
        game: {
          ...game,
          homeTeam: game.homeTeam ?? null,
          awayTeam: game.awayTeam ?? null,
        },
      });
    }

    return results;
  }, [propDefinitionBets, propDefinitions, gameById]);

  const selectedLeague = useMemo(
    () => (leagues ?? []).find((league) => String(league.id) === selectedLeagueId) ?? null,
    [leagues, selectedLeagueId]
  );

  const leagueWeekGamesQueryKey = useMemo(
    () => [
      '/api/leagues',
      selectedLeagueId,
      'weeks',
      selectedWeekId ?? 'none',
      selectedLeague?.resolvedSeasonId ?? 'none',
      'games',
    ],
    [selectedLeague?.resolvedSeasonId, selectedLeagueId, selectedWeekId]
  );

  const resolveDefaultGameId = useCallback(() => {
    const availableGames = games ?? [];
    const matchesFilter = gameFilterId
      ? availableGames.some((game) => game.id === gameFilterId)
      : false;
    if (matchesFilter) {
      return gameFilterId;
    }
    return availableGames[0]?.id ?? '';
  }, [gameFilterId, games]);

  const resetForm = useCallback(() => {
    setFormState({
      gameId: resolveDefaultGameId(),
      label: '',
      description: '',
      options: [
        { side: 'one', label: '', odds: '' },
        { side: 'two', label: '', odds: '' },
      ],
      winningSide: '',
      outcomeLabel: '',
      propType: '',
      statType: '',
      threshold: '',
      teamSide: '',
      playerIds: '',
    });
    setEditingBetId(null);
    setIsAutoProp(false);
    setAutoPropSource('manual');
    setIsManualRoundProp(false);
    setKalshiUrl('');
    setAutoUpdateKalshiOddsUntilKickoff(false);
    setShowValidation(false);
  }, [resolveDefaultGameId]);

  useEffect(() => {
    if (!games) {
      return;
    }
    const availableGames = games;
    const hasCurrent = formState.gameId
      ? availableGames.some((game) => game.id === formState.gameId)
      : false;
    if (hasCurrent) {
      return;
    }
    setFormState((prev) => ({
      ...prev,
      gameId: resolveDefaultGameId(),
    }));
  }, [formState.gameId, games, resolveDefaultGameId]);

  const kalshiPreviewMutation = useMutation({
    mutationFn: async () => {
      return trpcClient.nfl.previewKalshiMarket.mutate({
        url: kalshiUrl.trim(),
      });
    },
    onSuccess: (preview: {
      title?: string;
      description?: string;
      options?: { side: string; label: string; odds: number }[];
    }) => {
      setFormState((prev) => ({
        ...prev,
        label: preview.title ?? prev.label,
        description: preview.description ?? prev.description,
        options: Array.isArray(preview.options) && preview.options.length >= 2
          ? preview.options.map((option) => ({
              side: option.side,
              label: option.label,
              odds: formatOddsInputValue(option.odds),
            }))
          : prev.options,
      }));
    },
    onError: (error) => {
      toast.show({
        title: 'Kalshi Preview Failed',
        description: error instanceof Error ? error.message : 'Failed to preview Kalshi market',
        variant: 'destructive',
      });
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const outcomeLabel = '';
      const winningSide = '';
      const manualRoundAnchorGameId = manualRoundGameIds[0] ?? '';
      const resolvedManualGameId = isManualRoundProp ? manualRoundAnchorGameId : formState.gameId;

      if (!resolvedManualGameId || !formState.label.trim()) {
        throw new Error('Missing required fields');
      }

      if (isManualRoundProp && manualRoundGameIds.length === 0) {
        throw new Error('Round custom props require at least one game in the selected week');
      }

      const optionsPayload = formState.options.map(o => ({
        label: o.label.trim(),
        odds: parseOddsValue(o.odds),
        side: o.side,
        ...(o.playerId ? { playerId: o.playerId } : {}),
        ...(o.outcome ? { outcome: o.outcome } : {}),
      }));

      if (optionsPayload.some(o => !o.label || o.odds === null)) {
        throw new Error('All options must have a label and valid odds');
      }

      if (optionsPayload.length < 2) {
        throw new Error('At least 2 options are required');
      }

      if (isAutoProp) {
        if (autoPropSource === 'kalshi') {
          if (!kalshiUrl.trim()) {
            throw new Error('Kalshi link is required');
          }
          return trpcClient.nfl.importKalshiProp.mutate({
            nflGameId: formState.gameId,
            url: kalshiUrl.trim(),
            label: formState.label.trim(),
            description: formState.description.trim(),
            options: optionsPayload,
            autoUpdateOddsUntilKickoff: autoUpdateKalshiOddsUntilKickoff,
          });
        }

        const playerIds = parsePlayerIds(formState.playerIds);
        const propType = formState.propType.trim();
        const statType = formState.statType.trim();
        const threshold = parseOddsValue(formState.threshold);
        const isSoccerProp = autoPropSource === 'soccer' || autoPropSource === 'round';
        const isRoundAutoProp = autoPropSource === 'round' || ROUND_PROP_TYPES.includes(propType as RoundPropMetric);
        const requiresPlayers = !isRoundAutoProp && !GAME_LEVEL_SOCCER_PROP_TYPES.includes(propType);

        if (!propType || (requiresPlayers && playerIds.length === 0)) {
          throw new Error('Missing auto prop fields');
        }

        if (!isSoccerProp && PLAYER_VS_PLAYER_PROP_TYPES.includes(propType) && playerIds.length < 2) {
          throw new Error('Player vs player props require two players');
        }

        if (!isSoccerProp && EXACTLY_TWO_PLAYER_PROP_TYPES.includes(propType) && playerIds.length !== 2) {
          throw new Error('Either-player props require exactly two players');
        }

        if (!isSoccerProp && MULTI_PLAYER_PROP_TYPES.includes(propType) && playerIds.length < 2) {
          throw new Error('This prop type requires at least two players');
        }

        if (!isSoccerProp && FIRST_PLAYER_PROP_TYPES.includes(propType)) {
          if (playerIds.length < 2) {
            throw new Error('First-player props require at least two players');
          }
          if (playerIds.length > 10) {
            throw new Error('First-player props support at most ten players');
          }
          if (statType !== 'touchdowns') {
            throw new Error('First-player props currently support touchdowns only');
          }
        }

        if (isSoccerProp && SOCCER_TWO_PLAYER_PROP_TYPES.includes(propType) && playerIds.length < 2) {
          throw new Error('This prop type requires at least two players');
        }

        if (PROP_TYPES_REQUIRING_STAT_TYPE.includes(propType) && !statType) {
          throw new Error('This prop type requires a stat type');
        }

        if (
          PROP_TYPES_REQUIRING_THRESHOLD.includes(propType) &&
          threshold === null &&
          !(propType === 'LONGEST_STAT' && playerIds.length >= 2)
        ) {
          throw new Error('This prop type requires a threshold');
        }

        if (
          SOCCER_PROP_TYPES_REQUIRING_TEAM_SIDE.includes(propType) &&
          formState.teamSide !== 'home' &&
          formState.teamSide !== 'away'
        ) {
          throw new Error('This prop type requires a team');
        }

        const resolvedStatType = PROP_TYPES_REQUIRING_STAT_TYPE.includes(propType)
          ? statType
          : PROP_TYPE_DEFAULT_STAT_TYPE[propType];
        const soccerPropSubtype =
          propType === 'SOCCER_BOTH_TEAMS_TO_SCORE'
            ? 'soccer_both_teams_to_score'
            : propType === 'SOCCER_CORNERS_OVER_UNDER'
              ? 'soccer_corners_over_under'
            : propType === 'SOCCER_FIRST_TEAM_TO_CORNERS'
              ? 'soccer_first_team_to_corners'
            : propType === 'SOCCER_FOULS_OVER_UNDER'
              ? 'soccer_fouls_over_under'
              : propType === 'SOCCER_TEAM_FOULS_OVER_UNDER'
                ? 'soccer_team_fouls_over_under'
              : propType === 'SOCCER_OFFSIDES_OVER_UNDER'
                ? 'soccer_offsides_over_under'
              : propType === 'SOCCER_TEAM_OFFSIDES_OVER_UNDER'
                ? 'soccer_team_offsides_over_under'
              : propType === 'SOCCER_TEAM_GOALS_ALLOWED_OVER_UNDER'
                ? 'soccer_team_goals_allowed_over_under'
              : propType === 'SOCCER_TEAM_SHOTS_ON_TARGET_PCT_OVER_UNDER'
                ? 'soccer_team_shots_on_target_pct_over_under'
              : propType === 'SOCCER_GOALS_ODD_EVEN'
                ? formState.teamSide === 'home' || formState.teamSide === 'away'
                  ? `soccer_${formState.teamSide}_goals_odd_even`
                  : 'soccer_match_goals_odd_even'
              : propType === 'SOCCER_METHOD_OF_VICTORY'
                ? 'soccer_method_of_victory'
              : propType === 'SOCCER_FIRST_GOAL_INTERVAL'
                ? 'soccer_first_goal_interval'
              : propType === 'SOCCER_FIRST_GOAL_METHOD'
                ? 'soccer_first_goal_method'
              : propType === 'SOCCER_FIRST_GOAL_SCORER'
                ? 'soccer_first_goal_scorer'
              : propType === 'SOCCER_PLAYER_OVER_UNDER'
                ? `soccer_player_${statType || 'stat'}_over_under`
              : propType === 'SOCCER_PLAYER_EACH_HALF_OVER_UNDER'
                ? `soccer_player_${statType || 'stat'}_each_half_over_under`
              : propType === 'SOCCER_PLAYERS_COMBINED_OVER_UNDER'
                ? `soccer_players_combined_${statType || 'stat'}_over_under`
              : propType === 'SOCCER_ALL_PLAYERS_OVER_UNDER'
                ? `soccer_all_players_${statType || 'stat'}_over_under`
              : propType === 'SOCCER_PLAYER_RACE_TO_STAT'
                ? `soccer_player_${statType || 'stat'}_race_to_stat`
              : propType === 'SOCCER_TEAM_RACE_TO_STAT'
                ? `soccer_team_${statType || 'stat'}_race_to_stat`
              : propType === 'SOCCER_BOTH_TEAMS_OVER_STAT'
                ? `soccer_both_teams_${statType || 'stat'}_over`
              : propType === 'SOCCER_PLAYER_STAT_COMPARISON'
                ? `soccer_player_${statType || 'stat'}_comparison`
              : propType === 'SOCCER_TEAM_STAT_COMPARISON'
                ? `soccer_team_${statType || 'stat'}_comparison`
                : undefined;
        const roundDefaults = ROUND_PROP_LABELS[propType as RoundPropMetric];
        const roundGameIds = isRoundAutoProp ? (games ?? []).map((game) => game.id).filter(Boolean) : [];

        if (isRoundAutoProp && roundGameIds.length === 0) {
          throw new Error('Round props require at least one game in the selected week');
        }

        return trpcClient.nfl.createPropDefinition.mutate({
          nflGameId: isRoundAutoProp ? roundGameIds[0] : formState.gameId,
          propType,
          statType: resolvedStatType || undefined,
          comparison:
            PROP_TYPES_REQUIRING_THRESHOLD.includes(propType) &&
            propType !== 'PLAYER_EITHER_REACHES_THRESHOLD'
              ? 'OVER'
              : undefined,
          threshold: threshold ?? undefined,
          displayConfig: {
            label: formState.label.trim(),
            description: formState.description.trim(),
            sideLabelOne: optionsPayload[0]!.label,
            sideLabelTwo: optionsPayload[1]!.label,
            options: optionsPayload,
            ...(isRoundAutoProp
              ? {
                  sport: 'soccer',
                  competition: 'fifa-world-cup',
                  propSubtype: roundDefaults?.metric,
                  roundProp: true,
                  roundPropMetric: roundDefaults?.metric,
                  roundGameIds,
                  threshold,
                  statsProvider: 'api-football',
                  oddsProvider: 'manual',
                  manualResolutionAllowed: true,
                }
              : {}),
            ...(propType === 'FIRST_PLAYER_TO_STAT'
              ? {
                  sport: 'nfl',
                  statsProvider: 'api-nfl',
                  oddsProvider: 'manual',
                  manualResolutionAllowed: true,
                  noneOutcomeSide: optionsPayload[optionsPayload.length - 1]?.side,
                }
              : {}),
            ...(isSoccerProp
              ? {
                  sport: 'soccer',
                  competition: 'fifa-world-cup',
                  propSubtype: isRoundAutoProp ? roundDefaults?.metric : soccerPropSubtype,
                  ...(propType === 'SOCCER_PLAYER_STAT_COMPARISON'
                    ? { comparisonEntityType: 'player' }
                    : {}),
                  ...(propType === 'SOCCER_TEAM_STAT_COMPARISON'
                    ? { comparisonEntityType: 'team' }
                    : {}),
                  ...(SOCCER_PROP_TYPES_REQUIRING_TEAM_SIDE.includes(propType)
                    ? { teamSide: formState.teamSide }
                    : {}),
                  ...(propType === 'SOCCER_GOALS_ODD_EVEN' &&
                  (formState.teamSide === 'home' || formState.teamSide === 'away')
                    ? { teamSide: formState.teamSide }
                    : {}),
                  statsProvider: 'api-football',
                  oddsProvider: 'manual',
                  manualResolutionAllowed: true,
                }
              : {}),
          },
          playerIds: requiresPlayers ? playerIds : [],
          roundGameIds,
          oddsOne: optionsPayload[0]!.odds,
          oddsTwo: optionsPayload[1]!.odds,
        });
      }

      return trpcClient.nfl.createCustomProp.mutate({
        gameId: resolvedManualGameId,
        label: formState.label.trim(),
        description: formState.description.trim(),
        // Pass dummy values for old schema compatibility, though backend should prioritize options
        sideLabelOne: optionsPayload[0]!.label,
        sideLabelTwo: optionsPayload[1]!.label,
        oddsOne: optionsPayload[0]!.odds,
        oddsTwo: optionsPayload[1]!.odds,
        options: optionsPayload,
        winningSide: winningSide || undefined,
        outcomeLabel: outcomeLabel || undefined,
        roundProp: isManualRoundProp,
        roundGameIds: isManualRoundProp ? manualRoundGameIds : undefined,
      });
    },
    onSuccess: (payload) => {
      const wasAutoProp = isAutoProp;
      queryClient.invalidateQueries(trpc.nfl.customProps.queryFilter());
      queryClient.invalidateQueries(trpc.nfl.propDefinitions.queryFilter());
      if (propDefinitionIds.length > 0) {
        queryClient.invalidateQueries(trpc.nfl.propBetsByDefinition.queryFilter());
      }
      resetForm();
      const createdBet = 'bet' in (payload as any) ? (payload as any).bet : payload;
      if (createdBet) {
        setEnableModalBet(createdBet as CustomPropBetRecord);
        setEnableModalVisible(true);
      }
      toast.show({
        title: wasAutoProp ? 'Auto Prop Created' : 'Custom Prop Created',
        description: wasAutoProp
          ? 'Your auto prop bet is ready.'
          : 'Your custom prop bet is ready.',
      });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({
          title: 'Unauthorized',
          description: 'You are logged out. Please log in again.',
          variant: 'destructive',
        });
        return;
      }
      toast.show({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create prop',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editingBetId) {
        throw new Error('No bet selected');
      }
      const outcomeLabel = formState.outcomeLabel.trim();
      const winningSide = formState.winningSide;

      if (
        !formState.gameId ||
        !formState.label.trim()
      ) {
        throw new Error('Missing required fields');
      }

      const optionsPayload = formState.options.map(o => ({
        label: o.label.trim(),
        odds: parseOddsValue(o.odds),
        side: o.side,
      }));

      if (optionsPayload.some(o => !o.label || o.odds === null)) {
        throw new Error('All options must have a label and valid odds');
      }
      
      if (optionsPayload.length < 2) {
        throw new Error('At least 2 options are required');
      }

      if (!canSetOutcome && (winningSide || outcomeLabel)) {
        throw new Error('Outcome can only be set for completed games');
      }


      return trpcClient.nfl.updateCustomProp.mutate({
        id: editingBetId,
        payload: {
          gameId: formState.gameId,
          label: formState.label.trim(),
          description: formState.description.trim(),
          sideLabelOne: optionsPayload[0]!.label,
          sideLabelTwo: optionsPayload[1]!.label,
          oddsOne: optionsPayload[0]!.odds,
          oddsTwo: optionsPayload[1]!.odds,
          options: optionsPayload,
          winningSide: winningSide || undefined,
          outcomeLabel: outcomeLabel || undefined,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.nfl.customProps.queryFilter());
      resetForm();
      toast.show({
        title: 'Custom Prop Updated',
        description: 'Your changes have been saved.',
      });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({
          title: 'Unauthorized',
          description: 'You are logged out. Please log in again.',
          variant: 'destructive',
        });
        return;
      }
      toast.show({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update custom prop',
        variant: 'destructive',
      });
    },
  });

  const handleEditBet = (bet: CustomPropBet) => {
    setEditingBetId(bet.id);
    setIsAutoProp(false);
    setSelectedPlayers([]);
    setPlayerSearchTerm('');
    
    // Normalize options from displayInfo or fallback to sideLabelOne/Two
    let options = bet.displayInfo.options || [];
    if (options.length === 0) {
      if (bet.displayInfo.sideLabelOne) {
        options.push({ side: 'one', label: bet.displayInfo.sideLabelOne, odds: bet.oddsOne ?? 0 });
      }
      if (bet.displayInfo.sideLabelTwo) {
        options.push({ side: 'two', label: bet.displayInfo.sideLabelTwo, odds: bet.oddsTwo ?? 0 });
      }
    }
    
    setFormState({
      gameId: bet.nflGameId,
      label: bet.displayInfo.label ?? '',
      description: bet.displayInfo.description ?? '',
      options: options.map(o => ({
        side: o.side,
        label: o.label,
        odds: o.odds.toString(),
      })),
      winningSide: bet.displayInfo.winningSide ?? '',
      outcomeLabel: bet.displayInfo.outcomeLabel ?? '',
      propType: '',
      statType: '',
      threshold: '',
      teamSide: '',
      playerIds: '',
    });
    setShowValidation(false);
    setEnableModalVisible(false);
    setEnableModalBet(null);
    setTabsDefault('manage');
    setTabsKey((prev) => prev + 1);
  };

  const enableBetMutation = useMutation({
    mutationFn: async ({ 
      betId,
      leagueId,
      seasonId,
      scoringConfigOverride,
      propOfTheWeek,
    }: { 
      betId: string;
      leagueId: string;
      seasonId?: string;
      scoringConfigOverride: Record<string, unknown> | null;
      propOfTheWeek: boolean;
    }) => {
      if (!leagueId) {
        throw new Error('Select a league to enable bets.');
      }
      return trpcClient.leagues.enableCustomProp.mutate({
        leagueId,
        betId,
        seasonId,
        scoringConfigOverride,
        propOfTheWeek,
      });
    },
    onSuccess: (payload: { alreadyEnabled?: boolean; propOfTheWeek?: boolean }) => {
      if (payload?.alreadyEnabled && !payload.propOfTheWeek) {
        toast.show({
          title: 'Duplicate bet',
          description: 'That bet is already in the selected league.',
          variant: 'destructive',
        });
        return;
      }
      queryClient.invalidateQueries({ queryKey: leagueWeekGamesQueryKey });
      setEnableModalVisible(false);
      setEnableModalBet(null);
      toast.show({
        title: payload.propOfTheWeek ? 'Prop of the Week Set' : 'Bet Added',
        description: payload.propOfTheWeek
          ? 'The bet is now the selected league’s prop of the week.'
          : 'The bet is now in the selected league.',
      });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({
          title: 'Unauthorized',
          description: 'You are logged out. Please log in again.',
          variant: 'destructive',
        });
        return;
      }
      toast.show({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to enable bet',
        variant: 'destructive',
      });
    },
  });


  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const playerIdsList = useMemo(() => parsePlayerIds(formState.playerIds), [formState.playerIds]);
  const thresholdValue = useMemo(() => parseOddsValue(formState.threshold), [formState.threshold]);
  const requiredFieldState = useMemo(
    () => ({
      gameId: isManualRoundProp ? manualRoundGameIds.length === 0 : !formState.gameId,
      label: !formState.label.trim(),
      options: formState.options.some(o => !o.label.trim() || parseOddsValue(o.odds) === null),
      propType: isAutoProp && autoPropSource !== 'kalshi' && !formState.propType.trim(),
      playerIds:
        isAutoProp &&
        autoPropSource !== 'kalshi' &&
        !isGameLevelSoccerProp &&
        (EXACTLY_TWO_PLAYER_PROP_TYPES.includes(formState.propType.trim())
          ? playerIdsList.length !== 2
          : FIRST_PLAYER_PROP_TYPES.includes(formState.propType.trim())
          ? playerIdsList.length < 2
          : playerIdsList.length === 0),
      teamSide:
        isAutoProp &&
        SOCCER_PROP_TYPES_REQUIRING_TEAM_SIDE.includes(formState.propType.trim()) &&
        formState.teamSide !== 'home' &&
        formState.teamSide !== 'away',
      statType:
        isAutoProp &&
        PROP_TYPES_REQUIRING_STAT_TYPE.includes(formState.propType.trim()) &&
        !formState.statType.trim(),
      threshold:
        isAutoProp &&
        PROP_TYPES_REQUIRING_THRESHOLD.includes(formState.propType.trim()) &&
        !(formState.propType.trim() === 'LONGEST_STAT' && playerIdsList.length >= 2) &&
        thresholdValue === null,
    }),
    [
      formState.gameId,
      isManualRoundProp,
      manualRoundGameIds.length,
      formState.label,
      formState.options,
      formState.propType,
      formState.statType,
      formState.teamSide,
      autoPropSource,
      isAutoProp,
      isGameLevelSoccerProp,
      playerIdsList,
      thresholdValue,
    ]
  );
  const hasRequiredErrors = Object.values(requiredFieldState).some(Boolean);

  const handleAddOption = () => {
    setFormState(prev => {
      // If we are at 11, stop.
      if (prev.options.length >= 11) return prev;
      
      // Ensure we don't duplicate keys if user deleted some
      const usedSides = new Set(prev.options.map(o => o.side));
      const allPossibleSides = ['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven'];
      const nextSide = allPossibleSides.find(s => !usedSides.has(s));
      
      if (!nextSide) return prev;

      return {
        ...prev,
        options: [
          ...prev.options,
          { side: nextSide, label: '', odds: '' }
        ]
      };
    });
  };

  const handleRemoveOption = (index: number) => {
    setFormState(prev => {
      if (prev.options.length <= 2) return prev; // Min 2 options
      const newOptions = [...prev.options];
      newOptions.splice(index, 1);
      return { ...prev, options: newOptions };
    });
  };

  const handleOptionChange = (index: number, field: 'label' | 'odds', value: string) => {
    setFormState((prev) => {
      const newOptions = [...prev.options];
      const option = newOptions[index];
      if (option) {
        newOptions[index] = { ...option, [field]: value };
      }
      return { ...prev, options: newOptions };
    });
  };

  const handleSubmit = () => {
    setShowValidation(true);
    if (hasRequiredErrors) {
      toast.show({
        title: 'Missing required fields',
        description: 'Please complete the highlighted fields before saving.',
        variant: 'destructive',
      });
      return;
    }
    if (editingBetId) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  const enableInFlightBetId = enableBetMutation.variables?.betId;

  const closeEnableModal = () => {
    setEnableModalVisible(false);
    setEnableModalBet(null);
  };

  const openEnableModal = (bet: CustomPropBetRecord, forceYes = false) => {
    setEnableModalBet(bet);
    setEnableModalChoice(forceYes ? 'yes' : enableModalChoice);
    setEnableModalVisible(true);
  };

  const overrideSides = useMemo(
    () => getOverrideSides(enableModalBet?.displayInfo ?? null),
    [enableModalBet?.displayInfo]
  );

  useEffect(() => {
    if (!enableModalBet) {
      setOverrideSideValues({});
      return;
    }
    const initialValues = Object.fromEntries(overrideSides.map((side) => [side.side, '']));
    setOverrideSideValues(initialValues);
  }, [enableModalBet, overrideSides]);

  // ... rest of the file ... (buildScoringOverride and others seem fine to keep)


  const buildScoringOverride = () => {
    if (overrideMode === 'default') {
      return null;
    }
    if (overrideMode === 'multiplier') {
      const multiplier = Number.parseFloat(overrideMultiplier);
      if (!Number.isFinite(multiplier) || multiplier < 0) {
        return null;
      }
      return { type: 'oddsBased', multiplier };
    }
    if (overrideSides.length === 0) {
      return null;
    }
    const result: Record<string, number> = {};
    for (const side of overrideSides) {
      const rawValue = overrideSideValues[side.side];
      const parsed = Number.parseFloat(rawValue ?? '');
      if (!Number.isFinite(parsed)) {
        return null;
      }
      result[side.side] = parsed;
    }
    return { type: 'nPoints', ...result };
  };

  const fixedOverrideFields =
    overrideMode === 'fixed' ? (
      <Box gap="m" marginBottom="m">
        {overrideSides.map((side) => (
          <Box key={side.side} gap="s">
            <Text variant="bodySmall" fontWeight="600" color="mutedForeground">
              {side.label} Points
            </Text>
            <Input
              value={overrideSideValues[side.side] ?? ''}
              onChangeText={(value) =>
                setOverrideSideValues((prev) => ({
                  ...prev,
                  [side.side]: value,
                }))
              }
              placeholder="e.g. 2"
              keyboardType="decimal-pad"
            />
          </Box>
        ))}
      </Box>
    ) : null;

  const handleEnableSubmit = () => {
    if (!enableModalBet) {
      closeEnableModal();
      return;
    }
    if (enableModalChoice === 'no') {
      closeEnableModal();
      return;
    }
    if (!enableLeagueId) {
      toast.show({
        title: 'Select a league',
        description: 'Choose a league to enable this bet.',
        variant: 'destructive',
      });
      return;
    }
    const scoringOverride = buildScoringOverride();
    if (overrideMode !== 'default' && !scoringOverride) {
      toast.show({
        title: 'Invalid scoring override',
        description: 'Enter valid values for the scoring override.',
        variant: 'destructive',
      });
      return;
    }
    const seasonId = leagues?.find((league) => String(league.id) === enableLeagueId)?.resolvedSeasonId;
    setSelectedLeagueId(enableLeagueId);
    const payload: { 
      betId: string;
      leagueId: string;
      seasonId?: string;
      scoringConfigOverride: Record<string, unknown> | null;
      propOfTheWeek: boolean;
    } = {
      betId: enableModalBet.id,
      leagueId: enableLeagueId,
      scoringConfigOverride: scoringOverride,
      propOfTheWeek: enableModalChoice === 'propOfTheWeek',
    };
    if (seasonId) {
      payload.seasonId = seasonId;
    }
    enableBetMutation.mutate(payload);
  };

  const needsResolutionBets = useMemo(() => {
    const now = getCurrentTime();
    return (customProps ?? []).filter((bet) => {
      const startTime = new Date(bet.game.gameTime);
      if (Number.isNaN(startTime.getTime())) {
        return false;
      }
      if (startTime > now) {
        return false;
      }
      const hasOutcome = Boolean(bet.displayInfo.winningSide && bet.displayInfo.outcomeLabel);
      return !hasOutcome;
    });
  }, [customProps]);

  const renderEnableButton = (bet: CustomPropBet) => {
    const isEnabling = enableBetMutation.isPending && enableInFlightBetId === bet.id;
    const label = isEnabling ? 'Adding...' : 'Add to my league';
    return (
      <Button
        variant="default"
        onPress={() => {
          openEnableModal({ id: bet.id, nflGameId: bet.nflGameId, displayInfo: bet.displayInfo }, true);
        }}
        disabled={enableBetMutation.isPending}
        style={minWidth90Style}
      >
        <Text color="textInverse">{label}</Text>
      </Button>
    );
  };

  if (!isBetsAdmin) {
    return <BetsCreatorRestrictedState />;
  }

  return (
    <Box flex={1} backgroundColor="background">
      <AppHeader title="Bets Creator" />
      <KeyboardAvoidingView
        behavior={isIOS ? 'padding' : undefined}
        style={flexStyle}
      >
        <ScrollView
          style={flexStyle}
          contentContainerStyle={betEditorScrollContentStyle}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
        >
          <Box marginBottom="m">
            <Text variant="h2" fontWeight="bold" color="text">Bets Creator</Text>
            <Text variant="body" color="mutedForeground" lineHeight={20}>
              Create, edit, and search custom and auto prop bets for a specific NFL week.
            </Text>
          </Box>

          <Box marginBottom="m">
            <Card onLayout={logLayout('WeekCard')}>
              <CardHeader onLayout={logLayout('WeekCardHeader')}>
                <CardTitle>
                  <Text variant="title" fontWeight="600" color="text">Week</Text>
                </CardTitle>
              </CardHeader>
              <CardContent onLayout={logLayout('WeekCardContent')}>
                <Box gap="s" marginBottom="m">
                  <Text variant="bodySmall" fontWeight="600" color="mutedForeground">Week</Text>
                  <AppSelect
                    value={selectedWeekId ?? ''}
                    onChange={(value) => setSelectedWeekId(value || null)}
                    options={(weeks ?? []).map((week) => ({
                      value: week.id,
                      label: `Week ${week.week}: ${week.startDate} - ${week.endDate}`,
                    }))}
                    placeholder={currentWeek ? `Week ${currentWeek.week}` : 'Choose a week'}
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box marginBottom="m">
            <Tabs key={`${tabsKey}-${tabsDefault}`} defaultValue={tabsDefault}>
              <TabsList>
                <TabsTrigger value="manage">
                  <Text variant="body" fontWeight="600" color="text">Create / Edit</Text>
                </TabsTrigger>
                <TabsTrigger value="search">
                  <Text variant="body" fontWeight="600" color="text">Search</Text>
                </TabsTrigger>
                <TabsTrigger value="resolution">
                  <Text variant="body" fontWeight="600" color="text">Needs Resolution</Text>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="manage">
                <Card>
                      <CardHeader>
                        <CardTitle>
                          <Text variant="title" fontWeight="600" color="text">
                            {editingBetId
                              ? 'Edit Custom Prop'
                              : autoPropSource === 'kalshi'
                                ? 'Import Kalshi Market'
                              : autoPropSource === 'round'
                                ? 'Create Round Prop'
                              : isAutoProp
                                ? 'Create Auto Prop'
                                : 'Create Custom Prop'}
                          </Text>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Box gap="s" marginBottom="m">
                          <Text variant="bodySmall" fontWeight="600" color="mutedForeground">
                            Prop Source
                          </Text>
                          <AppSelect
                            value={autoPropSource}
                            onChange={(value) => {
                              if (editingBetId) {
                                return;
                              }
                              setAutoPropSource((value || 'manual') as AutoPropSource);
                            }}
                            options={[
                              { value: 'manual', label: 'Manual Custom Prop' },
                              { value: 'nfl', label: 'NFL / API-NFL' },
                              { value: 'soccer', label: 'World Cup / Soccer' },
                              { value: 'round', label: 'World Cup / Round' },
                              { value: 'kalshi', label: 'Kalshi Market Link' },
                            ]}
                            disabled={Boolean(editingBetId)}
                          />
                          <Text variant="caption" color="mutedForeground">
                            World Cup props auto-update when provider data is available and remain manually resolvable otherwise. Round props use every game in the selected week.
                          </Text>
                          {editingBetId ? (
                            <Text variant="caption" color="mutedForeground">
                              Auto props can only be created, not edited.
                            </Text>
                          ) : null}
                        </Box>

                        {autoPropSource === 'manual' && !editingBetId ? (
                          <Box
                            flexDirection="row"
                            alignItems="center"
                            justifyContent="space-between"
                            gap="m"
                            marginBottom="m"
                          >
                            <Box flex={1} gap="xxs">
                              <Text variant="bodySmall" fontWeight="600" color="text">
                                Round prop
                              </Text>
                              <Text variant="caption" color="mutedForeground">
                                Applies this manual custom prop to every game in the selected week.
                              </Text>
                            </Box>
                            <Switch checked={isManualRoundProp} onCheckedChange={setIsManualRoundProp} />
                          </Box>
                        ) : null}

                        {autoPropSource === 'kalshi' ? (
                          <Box gap="s" marginBottom="m">
                            <Text variant="bodySmall" fontWeight="600" color="mutedForeground">
                              Kalshi Link
                            </Text>
                            <Input
                              placeholder="Paste a Kalshi market URL or ticker"
                              value={kalshiUrl}
                              onChangeText={setKalshiUrl}
                              autoCapitalize="none"
                              autoCorrect={false}
                            />
                            <Button
                              variant="outline"
                              onPress={() => kalshiPreviewMutation.mutate()}
                              disabled={!kalshiUrl.trim() || kalshiPreviewMutation.isPending}
                            >
                              <Text variant="body" fontWeight="600" color="text">
                                {kalshiPreviewMutation.isPending ? 'Loading Market...' : 'Preview From Kalshi'}
                              </Text>
                            </Button>
                            <Box flexDirection="row" alignItems="center" justifyContent="space-between" gap="m">
                              <Box flex={1} gap="xxs">
                                <Text variant="bodySmall" fontWeight="600" color="text">
                                  Auto-update odds until kickoff
                                </Text>
                                <Text variant="caption" color="mutedForeground">
                                  Kalshi prices will refresh until the associated game starts.
                                </Text>
                              </Box>
                              <Switch
                                checked={autoUpdateKalshiOddsUntilKickoff}
                                onCheckedChange={setAutoUpdateKalshiOddsUntilKickoff}
                              />
                            </Box>
                          </Box>
                        ) : null}

                        {isAutoProp && autoPropSource !== 'kalshi' ? (
                          <Box gap="s" marginBottom="m">
                            <Box>
                              <Box flexDirection="row" alignItems="center" gap="xs">
                                <Text variant="bodySmall" fontWeight="600" color="mutedForeground">Prop Type</Text>
                                <Text variant="bodySmall" fontWeight="bold" color={theme.colors.destructive}>*</Text>
                              </Box>
                              {showValidation && requiredFieldState.propType ? (
                                <Text variant="caption" color={theme.colors.destructive}>Required</Text>
                              ) : null}
                              <AppSelect
                                value={formState.propType}
                                onChange={(value) =>
                                  setFormState((prev) => ({ ...prev, propType: value }))
                                }
                                options={propTypeOptions}
                                placeholder="Select a prop type"
                              />
                            </Box>

                            {PROP_TYPES_REQUIRING_STAT_TYPE.includes(formState.propType.trim()) ? (
                              <Box>
                                <Box flexDirection="row" alignItems="center" gap="xs">
                                  <Text variant="bodySmall" fontWeight="600" color="mutedForeground">Stat Type</Text>
                                  <Text
                                    variant="bodySmall"
                                    fontWeight="bold"
                                    color={theme.colors.destructive}
                                  >
                                    *
                                  </Text>
                                </Box>
                                {showValidation && requiredFieldState.statType ? (
                                  <Text variant="caption" color={theme.colors.destructive}>Required</Text>
                                ) : null}
                                <AppSelect
                                  value={formState.statType}
                                  onChange={(value) =>
                                    setFormState((prev) => ({ ...prev, statType: value }))
                                  }
                                  options={statTypeOptions}
                                  placeholder="Select a stat type"
                                />
                              </Box>
                            ) : null}

                            {PROP_TYPES_REQUIRING_THRESHOLD.includes(formState.propType.trim()) &&
                            !(formState.propType.trim() === 'LONGEST_STAT' && playerIdsList.length >= 2) ? (
                              <Box>
                                <Box flexDirection="row" alignItems="center" gap="xs">
                                  <Text variant="bodySmall" fontWeight="600" color="mutedForeground">Threshold</Text>
                                  <Text
                                    variant="bodySmall"
                                    fontWeight="bold"
                                    color={theme.colors.destructive}
                                  >
                                    *
                                  </Text>
                                </Box>
                                {showValidation && requiredFieldState.threshold ? (
                                  <Text variant="caption" color={theme.colors.destructive}>Required</Text>
                                ) : null}
                                <Input
                                  placeholder="e.g. 50.5"
                                  value={formState.threshold}
                                  onChangeText={(value) =>
                                    setFormState((prev) => ({ ...prev, threshold: value }))
                                  }
                                  keyboardType="decimal-pad"
                                />
                              </Box>
                            ) : null}

                            {SOCCER_PROP_TYPES_REQUIRING_TEAM_SIDE.includes(formState.propType.trim()) ||
                            formState.propType.trim() === 'SOCCER_GOALS_ODD_EVEN' ? (
                              <Box>
                                <Box flexDirection="row" alignItems="center" gap="xs">
                                  <Text variant="bodySmall" fontWeight="600" color="mutedForeground">
                                    {formState.propType.trim() === 'SOCCER_GOALS_ODD_EVEN' ? 'Scope' : 'Team'}
                                  </Text>
                                  {SOCCER_PROP_TYPES_REQUIRING_TEAM_SIDE.includes(formState.propType.trim()) ? (
                                    <Text
                                      variant="bodySmall"
                                      fontWeight="bold"
                                      color={theme.colors.destructive}
                                    >
                                      *
                                    </Text>
                                  ) : null}
                                </Box>
                                {showValidation && requiredFieldState.teamSide ? (
                                 <Text variant="caption" color={theme.colors.destructive}>Required</Text>
                                ) : null}
                                <AppSelect
                                  value={formState.teamSide}
                                  onChange={(value) =>
                                    setFormState((prev) => ({ ...prev, teamSide: value }))
                                  }
                                  options={soccerTeamSideOptions}
                                  placeholder={
                                    formState.propType.trim() === 'SOCCER_GOALS_ODD_EVEN'
                                      ? 'Select a scope'
                                      : 'Select a team'
                                  }
                                />
                              </Box>
                            ) : null}

                            {!isGameLevelSoccerProp ? (
                            <Box>
                              <Box flexDirection="row" alignItems="center" gap="xs">
                                <Text variant="bodySmall" fontWeight="600" color="mutedForeground">Player IDs</Text>
                                <Text variant="bodySmall" fontWeight="bold" color={theme.colors.destructive}>*</Text>
                              </Box>
                              {showValidation && requiredFieldState.playerIds ? (
                                <Text variant="caption" color={theme.colors.destructive}>Required</Text>
                              ) : null}
                              <Input
                                placeholder="Search player name"
                                value={playerSearchTerm}
                                onChangeText={setPlayerSearchTerm}
                                onFocus={() => {
                                  isSelectingPlayerRef.current = false;
                                  if (playerSearchBlurTimeoutRef.current) {
                                    clearTimeout(playerSearchBlurTimeoutRef.current);
                                    playerSearchBlurTimeoutRef.current = null;
                                  }
                                  setIsPlayerSearchFocused(true);
                                }}
                                onBlur={() => {
                                  if (playerSearchBlurTimeoutRef.current) {
                                    clearTimeout(playerSearchBlurTimeoutRef.current);
                                  }
                                  playerSearchBlurTimeoutRef.current = setTimeout(() => {
                                    if (isSelectingPlayerRef.current) {
                                      return;
                                    }
                                    setIsPlayerSearchFocused(false);
                                  }, 150);
                                }}
                              />
                              {EXACTLY_TWO_PLAYER_PROP_TYPES.includes(formState.propType.trim()) ? (
                                <Text variant="caption" color="mutedForeground">
                                  Select two players. Yes wins if either one reaches the stat line.
                                </Text>
                              ) : null}
                              {selectedGameTeamAbbrs.length === 0 ? (
                                <Text variant="caption" color="mutedForeground">
                                  Select a game to filter players by team.
                                </Text>
                              ) : showPlayerResults ? (
                                playerSearchLoading ? (
                                  <Text variant="caption" color="mutedForeground">Searching players...</Text>
                                ) : availablePlayerResults.length > 0 ? (
                                  <Box gap="xs" marginTop="xs">
                                    {availablePlayerResults.map((player) => (
                                      <PressableBox
                                        key={player.id}
                                        paddingVertical="xs"
                                        paddingHorizontal="s"
                                        borderRadius="s"
                                        borderWidth={1}
                                        borderColor="border"
                                        onPressIn={() => {
                                          isSelectingPlayerRef.current = true;
                                          if (playerSearchBlurTimeoutRef.current) {
                                            clearTimeout(playerSearchBlurTimeoutRef.current);
                                            playerSearchBlurTimeoutRef.current = null;
                                          }
                                        }}
                                        onPress={() => {
                                          setSelectedPlayers((prev) => {
                                            const next = [...prev, player];
                                            setFormState((current) => ({
                                              ...current,
                                              playerIds: next.map((entry) => entry.id).join(', '),
                                            }));
                                            return next;
                                          });
                                          setPlayerSearchTerm('');
                                          setPlayerSearchQuery('');
                                          setIsPlayerSearchFocused(false);
                                          Keyboard.dismiss();
                                          setTimeout(() => {
                                            isSelectingPlayerRef.current = false;
                                          }, 0);
                                        }}
                                      >
                                        <Text variant="bodySmall" fontWeight="600" color="text">
                                          {player.fullName}
                                        </Text>
                                        <Text variant="caption" color="mutedForeground">
                                          {`${player.teamAbbr} • ${player.position}${player.jerseyNumber ? ` #${player.jerseyNumber}` : ''}`}
                                        </Text>
                                      </PressableBox>
                                    ))}
                                  </Box>
                                ) : (
                                  <Text variant="caption" color="mutedForeground">No players found.</Text>
                                )
                              ) : EXACTLY_TWO_PLAYER_PROP_TYPES.includes(formState.propType.trim()) && selectedPlayers.length >= 2 ? (
                                <Text variant="caption" color="mutedForeground">Two players selected.</Text>
                              ) : (
                                <Text variant="caption" color="mutedForeground">Type at least 2 characters.</Text>
                              )}

                              {selectedPlayers.length > 0 ? (
                                <Box gap="xs" marginTop="s">
                                  {selectedPlayers.map((player) => (
                                    <Box
                                      key={player.id}
                                      flexDirection="row"
                                      alignItems="center"
                                      justifyContent="space-between"
                                      paddingVertical="xs"
                                      paddingHorizontal="s"
                                      borderRadius="s"
                                      borderWidth={1}
                                      borderColor="border"
                                    >
                                      <Box>
                                        <Text variant="bodySmall" fontWeight="600" color="text">
                                          {player.fullName}
                                        </Text>
                                        <Text variant="caption" color="mutedForeground">
                                          {player.teamAbbr} • {player.position}
                                        </Text>
                                      </Box>
                                      <PressableBox
                                        onPress={() =>
                                          setSelectedPlayers((prev) => {
                                            const next = prev.filter((entry) => entry.id !== player.id);
                                            setFormState((current) => ({
                                              ...current,
                                              playerIds: next.map((entry) => entry.id).join(', '),
                                            }));
                                            return next;
                                          })
                                        }
                                      >
                                        <Trash2 size={16} color={theme.colors.mutedForeground} />
                                      </PressableBox>
                                    </Box>
                                  ))}
                                </Box>
                              ) : null}

                              <Input
                                placeholder="Selected player IDs"
                                value={formState.playerIds}
                                editable={false}
                              />
                            </Box>
                            ) : null}
                          </Box>
                        ) : null}

                        <Box gap="s" marginBottom="m">
                  <Box flexDirection="row" alignItems="center" gap="xs">
                    <Text variant="bodySmall" fontWeight="600" color="mutedForeground">
                      {isManualRoundProp ? 'Round Scope' : 'Associated Game'}
                    </Text>
                    <Text variant="bodySmall" fontWeight="bold" color={theme.colors.destructive}>*</Text>
                  </Box>
                  {showValidation && requiredFieldState.gameId ? (
                    <Text variant="caption" color={theme.colors.destructive}>Required</Text>
                  ) : null}
                  {isManualRoundProp ? (
                    <Text variant="bodySmall" color="mutedForeground">
                      {manualRoundGameIds.length > 0
                        ? `${manualRoundGameIds.length} games in selected week`
                        : 'Select a week with games to create a round prop'}
                    </Text>
                  ) : (
                    <AppSelect
                      value={formState.gameId}
                      onChange={(value) =>
                        setFormState((prev) => ({ ...prev, gameId: value }))
                      }
                      options={(games ?? []).map((game) => ({
                        value: game.id,
                        label: `${formatGameLabel(game)} • ${formatGameTime(game.gameTime)}`,
                      }))}
                      placeholder="Select a game"
                      triggerStyle={
                        showValidation && requiredFieldState.gameId
                          ? { borderColor: theme.colors.destructive, backgroundColor: theme.colors.destructive + '10' }
                          : undefined
                      }
                    />
                  )}
                </Box>

                <Box gap="s" marginBottom="m">
                  <Box flexDirection="row" alignItems="center" gap="xs">
                    <Text variant="bodySmall" fontWeight="600" color="mutedForeground">Label</Text>
                    <Text variant="bodySmall" fontWeight="bold" color={theme.colors.destructive}>*</Text>
                  </Box>
                  {showValidation && requiredFieldState.label ? (
                    <Text variant="caption" color={theme.colors.destructive}>Required</Text>
                  ) : null}
                  <Input
                    placeholder="e.g. Anytime TD Scorer"
                    value={formState.label}
                    onChangeText={(value) => setFormState((prev) => ({ ...prev, label: value }))}
                    style={
                      showValidation && requiredFieldState.label
                        ? { borderColor: theme.colors.destructive, backgroundColor: theme.colors.destructive + '10' }
                        : undefined
                    }
                  />
                </Box>

                <Box gap="s" marginBottom="m">
                  <Text variant="bodySmall" fontWeight="600" color="mutedForeground">Description</Text>
                  <Textarea
                    placeholder="Add context for the prop bet"
                    value={formState.description}
                    onChangeText={(value) =>
                      setFormState((prev) => ({ ...prev, description: value }))
                    }
                  />
                </Box>

                  <Box marginBottom="m">
                  <Box flexDirection="row" alignItems="center" justifyContent="space-between" marginBottom="s">
                    <Text variant="bodySmall" fontWeight="600" color="mutedForeground">Options</Text>
                    {formState.options.length < 10 && (
                      <PressableBox flexDirection="row" alignItems="center" onPress={handleAddOption}>
                        <Plus size={16} color={theme.colors.press} />
                        <Text variant="bodySmall" color="text" marginLeft="xs">Add Option</Text>
                      </PressableBox>
                    )}
                  </Box>
                  
                  <Box gap="m">
                    {formState.options.map((option, index) => (
                      <Box key={option.side} flexDirection="row" gap="m" alignItems="flex-start">
                        <Box flex={2} gap="xs">
                          <Text variant="caption" color="mutedForeground">Label</Text>
                          <Input
                            placeholder={`Option ${index + 1}`}
                            value={option.label}
                            onChangeText={(val) => handleOptionChange(index, 'label', val)}
                            style={
                              showValidation && !option.label.trim()
                                ? { borderColor: theme.colors.destructive, backgroundColor: theme.colors.destructive + '10' }
                                : undefined
                            }
                          />
                        </Box>
                        <Box flex={1} gap="xs">
                          <Text variant="caption" color="mutedForeground">Odds</Text>
                          <Input
                            placeholder="+100"
                            keyboardType="numbers-and-punctuation"
                            value={option.odds}
                            onChangeText={(val) => handleOptionChange(index, 'odds', val)}
                            style={
                              showValidation && parseOddsValue(option.odds) === null
                                ? { borderColor: theme.colors.destructive, backgroundColor: theme.colors.destructive + '10' }
                                : undefined
                            }
                          />
                        </Box>
                        {formState.options.length > 2 && (
                          <PressableBox 
                            paddingTop="xl" 
                            justifyContent="center"
                            onPress={() => handleRemoveOption(index)}
                          >
                            <Trash2 size={20} color={theme.colors.mutedForeground} />
                          </PressableBox>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>

                {editingBetId ? (
                  <Box gap="s" marginBottom="m">
                    <Text variant="bodySmall" fontWeight="600" color="mutedForeground">
                      Outcome (completed bets)
                    </Text>
                    {canSetOutcome ? (
                      <>
                        <AppSelect
                          value={formState.winningSide}
                          onChange={(value) =>
                            setFormState((prev) => ({ ...prev, winningSide: value }))
                          }
                          options={[
                            { value: '', label: 'Unresolved' },
                            { value: 'push', label: 'Push (tie/no winner)' },
                            ...formState.options.map(o => ({
                              value: o.side,
                              label: `Winner: ${o.label || o.side}`
                            }))
                          ]}
                          placeholder="Select winning side"
                        />
                        <Input
                          placeholder="Outcome label (e.g. 3 touchdown scores)"
                          value={formState.outcomeLabel}
                          onChangeText={(value) =>
                            setFormState((prev) => ({ ...prev, outcomeLabel: value }))
                          }
                        />
                      </>
                    ) : (
                      <Text color="mutedForeground">
                        Outcome can be set once the game has started.
                      </Text>
                    )}
                  </Box>
                ) : null}

                <Box gap="m" marginTop="m">
                  <Button onPress={handleSubmit} disabled={isSubmitting} style={styles.primaryAction}>
                    <Text variant="button" color="text">
                      {isSubmitting
                        ? 'Saving...'
                        : editingBetId
                          ? 'Update Custom Prop'
                          : autoPropSource === 'round'
                            ? 'Create Round Prop'
                          : isAutoProp
                            ? 'Create Auto Prop'
                            : 'Create Custom Prop'}
                    </Text>
                  </Button>
                  {Boolean(editingBetId) && (
                    <Button variant="outline" onPress={resetForm} disabled={isSubmitting}>
                      <Text color="text">Cancel Edit</Text>
                    </Button>
                  )}
                </Box>
                      </CardContent>
                </Card>
              </TabsContent>

          <TabsContent value="search">
            <Card>
              <CardHeader>
                <CardTitle>
                  <Text variant="title" fontWeight="600" color="text">Filters</Text>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Box gap="s" marginBottom="m">
                  <Text variant="bodySmall" fontWeight="600" color="mutedForeground">Game</Text>
                  <AppSelect
                    value={gameFilterId}
                    onChange={(value) => setGameFilterId(value)}
                    options={[ 
                      { value: '', label: 'All games' },
                      ...(games ?? []).map((game) => ({
                        value: game.id,
                        label: `${formatGameLabel(game)} • ${formatGameTime(game.gameTime)}`,
                      })),
                    ]}
                    placeholder="All games"
                  />
                </Box>

                <Box gap="s" marginBottom="m">
                  <Text variant="bodySmall" fontWeight="600" color="mutedForeground">Search</Text>
                  <Input
                    ref={searchInputRef}
                    placeholder="Search label, description, or side labels"
                    value={searchTerm}
                    onChangeText={handleSearchChange}
                  />
                </Box>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <Text variant="title" fontWeight="600" color="text">Custom Props</Text>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {customPropsLoading ? (
                  <Text color="mutedForeground">Loading custom props...</Text>
                ) : (customProps?.length ?? 0) > 0 ? (
                  customProps?.map((bet) => (
                    <Box key={bet.id} flexDirection={isNarrowViewport ? 'column' : 'row'} alignItems={isNarrowViewport ? 'stretch' : 'center'} justifyContent="space-between" gap="m" borderBottomWidth={1} borderColor="border" paddingVertical="m">
                      <Box flex={isNarrowViewport ? undefined : 1} gap="xs">
                        <Text variant="body" fontWeight="600" color="text">
                          {bet.displayInfo.label}
                        </Text>
                        <Text variant="bodySmall" color="mutedForeground">
                          {bet.displayInfo.description || 'No description provided'}
                        </Text>
                        <Text variant="bodySmall" color="mutedForeground">
                          {`${bet.displayInfo.sideLabelOne} (${bet.oddsOne ?? '—'}) vs ${bet.displayInfo.sideLabelTwo} (${bet.oddsTwo ?? '—'})`}
                        </Text>
                        {bet.displayInfo.winningSide || bet.displayInfo.outcomeLabel ? (
                          <Text variant="bodySmall" fontWeight="600" color="text">
                            {[
                              bet.displayInfo.outcomeLabel ? `Outcome: ${bet.displayInfo.outcomeLabel}` : '',
                              bet.displayInfo.winningSide ? `Winner: ${resolveWinningSideLabel(bet)}` : ''
                            ].filter(Boolean).join(' • ')}
                          </Text>
                        ) : bet.game.isCompleted ? (
                          <Text variant="bodySmall" color={theme.colors.warning}>Outcome pending</Text>
                        ) : null}
                        <Text variant="bodySmall" color="mutedForeground">
                          {`${formatGameLabel(bet.game)} • ${formatGameTime(
                            bet.game.gameTime
                          )}`}
                        </Text>
                      </Box>
                      <Box flexDirection="row" alignItems="center" justifyContent="flex-end" gap="s" flexWrap="wrap">
                        {renderEnableButton(bet)}
                        <Button variant="outline" onPress={() => handleEditBet(bet)}>
                          <Text color="text">Edit</Text>
                        </Button>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Text color="mutedForeground">
                    No custom prop bets found for the selected filters.
                  </Text>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <Text variant="title" fontWeight="600" color="text">Auto Props</Text>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {autoPropsLoading ? (
                  <Text color="mutedForeground">Loading auto props...</Text>
                ) : autoPropBets.length > 0 ? (
                  autoPropBets.map((bet) => (
                    <Box key={bet.id} flexDirection={isNarrowViewport ? 'column' : 'row'} alignItems={isNarrowViewport ? 'stretch' : 'center'} justifyContent="space-between" gap="m" borderBottomWidth={1} borderColor="border" paddingVertical="m">
                      <Box flex={isNarrowViewport ? undefined : 1} gap="xs">
                        <Text variant="body" fontWeight="600" color="text">
                          {bet.displayInfo.label}
                        </Text>
                        <Text variant="bodySmall" color="mutedForeground">
                          {bet.displayInfo.description || 'No description provided'}
                        </Text>
                        <Text variant="bodySmall" color="mutedForeground">
                          {`${bet.displayInfo.sideLabelOne} (${bet.oddsOne ?? '—'}) vs ${bet.displayInfo.sideLabelTwo} (${bet.oddsTwo ?? '—'})`}
                        </Text>
                        {bet.displayInfo.winningSide || bet.displayInfo.outcomeLabel ? (
                          <Text variant="bodySmall" fontWeight="600" color="text">
                            {[
                              bet.displayInfo.outcomeLabel ? `Outcome: ${bet.displayInfo.outcomeLabel}` : '',
                              bet.displayInfo.winningSide ? `Winner: ${resolveWinningSideLabel(bet)}` : ''
                            ].filter(Boolean).join(' • ')}
                          </Text>
                        ) : null}
                        <Text variant="bodySmall" color="mutedForeground">
                          {`${formatGameLabel(bet.game)} • ${formatGameTime(
                            bet.game.gameTime
                          )}`}
                        </Text>
                        <Text variant="caption" color="mutedForeground">
                          {bet.displayInfo.requiresManualResolution
                            ? `Manual resolution required${bet.displayInfo.manualResolutionReason ? `: ${bet.displayInfo.manualResolutionReason}` : ''}`
                            : bet.displayInfo.manualResolutionAllowed
                              ? 'Auto-updates when provider data is available; manual fallback enabled'
                              : 'Auto-resolved'}
                        </Text>
                      </Box>
                      <Box flexDirection="row" alignItems="center" justifyContent="flex-end" gap="s" flexWrap="wrap">
                        {renderEnableButton(bet)}
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Text color="mutedForeground">
                    No auto prop bets found for the selected filters.
                  </Text>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="resolution">
            <Card>
              <CardHeader>
                <CardTitle>
                  <Text variant="title" fontWeight="600" color="text">Needs Resolution</Text>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {customPropsLoading ? (
                  <Text color="mutedForeground">Loading custom props...</Text>
                ) : (needsResolutionBets?.length ?? 0) > 0 ? (
                  needsResolutionBets.map((bet) => (
                    <Box key={bet.id} flexDirection={isNarrowViewport ? 'column' : 'row'} alignItems={isNarrowViewport ? 'stretch' : 'center'} justifyContent="space-between" gap="m" borderBottomWidth={1} borderColor="border" paddingVertical="m">
                      <Box flex={isNarrowViewport ? undefined : 1} gap="xs">
                        <Text variant="body" fontWeight="600" color="text">
                          {bet.displayInfo.label}
                        </Text>
                        <Text variant="bodySmall" color="mutedForeground">
                          {bet.displayInfo.description || 'No description provided'}
                        </Text>
                        <Text variant="bodySmall" color={theme.colors.warning}>
                          {bet.displayInfo.outcomeLabel 
                            ? `Current: ${bet.displayInfo.outcomeLabel} (Pending)` 
                            : 'Outcome pending'}
                        </Text>
                        <Text variant="bodySmall" color="mutedForeground">
                          {`${formatGameLabel(bet.game)} • ${formatGameTime(
                            bet.game.gameTime
                          )}`}
                        </Text>
                      </Box>
                      <Box flexDirection="row" alignItems="center" gap="s">
                        {renderEnableButton(bet)}
                        <Button variant="outline" onPress={() => handleEditBet(bet)}>
                          <Text color="text">Resolve</Text>
                        </Button>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Text color="mutedForeground">
                    No custom props need resolution for the selected filters.
                  </Text>
                )}
              </CardContent>
            </Card>
              </TabsContent>
            </Tabs>
          </Box>
        </ScrollView>
      </KeyboardAvoidingView>

      {isIOS || Platform.OS === 'android' ? (
        <Modal
          visible={enableModalVisible}
          transparent
          animationType="fade"
          onRequestClose={closeEnableModal}
        >
          <Box flex={1} alignItems="center" justifyContent="center" padding="l" style={styles.modalOverlay}>
            <Box width="100%" maxWidth={520} borderRadius="none" backgroundColor="surface" padding="l" gap="m" style={styles.modalPanel}>
              <Box flexDirection="row" alignItems="center" justifyContent="space-between">
                <Text variant="title" fontWeight="bold" color="text">Add to League?</Text>
                <PressableBox onPress={closeEnableModal} paddingHorizontal="s" paddingVertical="xs">
                  <Text variant="bodySmall" color="mutedForeground">Close</Text>
                </PressableBox>
              </Box>
              <Text variant="bodySmall" color="mutedForeground">
                {enableModalBet?.displayInfo.label ?? 'Custom Prop Bet'}
              </Text>

              <Box flexDirection="row" gap="s">
                <Button
                  variant={enableModalChoice === 'yes' ? 'default' : 'outline'}
                  onPress={() => setEnableModalChoice('yes')}
                  style={flexStyle}
                >
                  <Text color="text">
                    Add to league
                  </Text>
                </Button>
                <Button
                  variant={enableModalChoice === 'no' ? 'default' : 'outline'}
                  onPress={() => setEnableModalChoice('no')}
                  style={flexStyle}
                >
                  <Text color="text">
                    No thanks
                  </Text>
                </Button>
                <Button
                  variant={enableModalChoice === 'propOfTheWeek' ? 'default' : 'outline'}
                  onPress={() => setEnableModalChoice('propOfTheWeek')}
                  style={flexStyle}
                >
                  <Text color="text">Make prop of week</Text>
                </Button>
              </Box>

              {enableModalChoice === 'yes' || enableModalChoice === 'propOfTheWeek' ? (
                <Box gap="m">
                  {(leagues?.length ?? 0) > 0 ? (
                    <Box gap="s" marginBottom="m">
                      <Text variant="bodySmall" fontWeight="600" color="mutedForeground">Target League</Text>
                      <AppSelect
                        value={enableLeagueId}
                        onChange={(value) => {
                          setEnableLeagueId(value);
                          setSelectedLeagueId(value);
                        }}
                        options={(leagues ?? []).map((league) => ({
                          value: String(league.id),
                          label: league.name,
                        }))}
                        placeholder="Select a league"
                        portalHost="bets-modal-host"
                      />
                    </Box>
                  ) : (
                    <Text color="mutedForeground">
                      Join or create a league to enable this bet.
                    </Text>
                  )}

                  <Box gap="s" marginBottom="m">
                    <Text variant="bodySmall" fontWeight="600" color="mutedForeground">Scoring Override</Text>
                    <AppSelect
                      value={overrideMode}
                      onChange={(value) =>
                        setOverrideMode(value as 'default' | 'multiplier' | 'fixed')
                      }
                      options={[ 
                        { value: 'default', label: 'Season default' },
                        { value: 'multiplier', label: 'Odds multiplier' },
                        { value: 'fixed', label: 'Fixed points' },
                      ]}
                      placeholder="Select scoring override"
                      portalHost="bets-modal-host"
                    />
                  </Box>

                  {overrideMode === 'multiplier' ? (
                    <Box gap="s" marginBottom="m">
                      <Text variant="bodySmall" fontWeight="600" color="mutedForeground">Multiplier</Text>
                      <Input
                        value={overrideMultiplier}
                        onChangeText={setOverrideMultiplier}
                        placeholder="e.g. 1.5"
                        keyboardType="decimal-pad"
                      />
                    </Box>
                  ) : null}

                  {fixedOverrideFields}


                </Box>
              ) : null}

              <Box flexDirection="row" justifyContent="flex-end" gap="s">
                <Button variant="outline" onPress={closeEnableModal} style={minWidth120Style}>
                  <Text color="text">Cancel</Text>
                </Button>
                <Button
                  onPress={handleEnableSubmit}
                  style={minWidth120Style}
                  disabled={enableBetMutation.isPending}
                >
                  <Text color="text">
                    {enableModalChoice === 'no'
                      ? 'Done'
                      : enableBetMutation.isPending
                        ? 'Enabling...'
                        : enableModalChoice === 'propOfTheWeek'
                          ? 'Add as Prop of Week'
                          : 'Enable Bet'}
                  </Text>
                </Button>
              </Box>
            </Box>
            <PortalHost name="bets-modal-host" />
          </Box>
        </Modal>
      ) : (
        enableModalVisible && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={100}
            alignItems="center"
            justifyContent="center"
            padding="l"
            style={styles.modalOverlay}
          >
            <Box
              width="100%"
              maxWidth={520}
              borderRadius="none"
              backgroundColor="surface"
              padding="l"
              gap="m"
              style={[styles.modalPanel, styles.webModalPanel]}
            >
              <ScrollView>
              <Box flexDirection="row" alignItems="center" justifyContent="space-between">
                <Text variant="title" fontWeight="bold" color="text">Add to League?</Text>
                <PressableBox onPress={closeEnableModal} paddingHorizontal="s" paddingVertical="xs">
                  <Text variant="bodySmall" color="mutedForeground">Close</Text>
                </PressableBox>
              </Box>
              <Text variant="bodySmall" color="mutedForeground">
                {enableModalBet?.displayInfo.label ?? 'Custom Prop Bet'}
              </Text>

              <Box flexDirection="row" gap="s" marginTop="m">
                <Button
                  variant={enableModalChoice === 'yes' ? 'default' : 'outline'}
                  onPress={() => setEnableModalChoice('yes')}
                  style={flexStyle}
                >
                  <Text color="text">
                    Add to league
                  </Text>
                </Button>
                <Button
                  variant={enableModalChoice === 'no' ? 'default' : 'outline'}
                  onPress={() => setEnableModalChoice('no')}
                  style={flexStyle}
                >
                  <Text color="text">
                    No thanks
                  </Text>
                </Button>
                <Button
                  variant={enableModalChoice === 'propOfTheWeek' ? 'default' : 'outline'}
                  onPress={() => setEnableModalChoice('propOfTheWeek')}
                  style={flexStyle}
                >
                  <Text color="text">Make prop of week</Text>
                </Button>
              </Box>

              {enableModalChoice === 'yes' || enableModalChoice === 'propOfTheWeek' ? (
                <Box gap="m" marginTop="m">
                  {(leagues?.length ?? 0) > 0 ? (
                    <Box gap="s" marginBottom="m">
                      <Text variant="bodySmall" fontWeight="600" color="mutedForeground">Target League</Text>
                      <AppSelect
                        value={enableLeagueId}
                        onChange={(value) => {
                          setEnableLeagueId(value);
                          setSelectedLeagueId(value);
                        }}
                        options={(leagues ?? []).map((league) => ({
                          value: String(league.id),
                          label: league.name,
                        }))}
                        placeholder="Select a league"
                        portalHost="bets-modal-host"
                      />
                    </Box>
                  ) : (
                    <Text color="mutedForeground">
                      Join or create a league to enable this bet.
                    </Text>
                  )}

                  <Box gap="s" marginBottom="m">
                    <Text variant="bodySmall" fontWeight="600" color="mutedForeground">Scoring Override</Text>
                    <AppSelect
                      value={overrideMode}
                      onChange={(value) =>
                        setOverrideMode(value as 'default' | 'multiplier' | 'fixed')
                      }
                      options={[ 
                        { value: 'default', label: 'Season default' },
                        { value: 'multiplier', label: 'Odds multiplier' },
                        { value: 'fixed', label: 'Fixed points' },
                      ]}
                      placeholder="Select scoring override"
                      portalHost="bets-modal-host"
                    />
                  </Box>

                  {overrideMode === 'multiplier' ? (
                    <Box gap="s" marginBottom="m">
                      <Text variant="bodySmall" fontWeight="600" color="mutedForeground">Multiplier</Text>
                      <Input
                        value={overrideMultiplier}
                        onChangeText={setOverrideMultiplier}
                        placeholder="e.g. 1.5"
                        keyboardType="decimal-pad"
                      />
                    </Box>
                  ) : null}

                  {fixedOverrideFields}


                </Box>
              ) : null}

              <Box flexDirection="row" justifyContent="flex-end" gap="s" marginTop="m">
                <Button variant="outline" onPress={closeEnableModal} style={minWidth120Style}>
                  <Text color="text">Cancel</Text>
                </Button>
                <Button
                  onPress={handleEnableSubmit}
                  style={minWidth120Style}
                  disabled={enableBetMutation.isPending}
                >
                  <Text color="text">
                    {enableModalChoice === 'no'
                      ? 'Done'
                      : enableBetMutation.isPending
                        ? 'Enabling...'
                        : enableModalChoice === 'propOfTheWeek'
                          ? 'Add as Prop of Week'
                          : 'Enable Bet'}
                  </Text>
                </Button>
              </Box>
              </ScrollView>
              <PortalHost name="bets-modal-host" />
            </Box>
          </Box>
        )
      )}
    </Box>
  );
}

const styles = StyleSheet.create(theme => ({
  modalOverlay: {
    backgroundColor: 'rgba(22, 36, 28, 0.58)',
  },
  modalPanel: {
    borderColor: theme.colors.press,
    borderRadius: 0,
    borderWidth: 3,
    shadowColor: theme.colors.press,
    shadowOffset: { width: 10, height: 10 },
    shadowOpacity: 1,
    shadowRadius: 0,
  },
  primaryAction: {
    backgroundColor: theme.colors.acid,
    borderColor: theme.colors.press,
    borderWidth: 2,
  },
  webModalPanel: {
    maxHeight: '90%',
    overflow: 'hidden',
  },
}));
